import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { useToast } from '../hooks/use-toast';
import {
  adminMe, clearAdminToken, adminStats, listApplications, getApplication,
  updateStatus, addNote, downloadFile, downloadApplicationsCsv,
  downloadReceipt, regenerateReceipt, refundApplication, getRefundPolicy,
  getRefundAnalytics, downloadRefundCsv,
  listCustomers, customerDetail, customerDeletionImpact, deleteCustomer,
  applicationDeletionImpact, deleteApplication, archiveApplication, unarchiveApplication,
  bulkDeleteApplicationsPreview, bulkDeleteApplications,
  getEvidence, updateEvidence, downloadEvidencePdf, getEvidencePdfBlobUrl,
  listEvidenceSupporting, uploadEvidenceSupporting,
  downloadEvidenceSupporting, deleteEvidenceSupporting,
  downloadEvidencePack,
  finaliseEvidence, reopenEvidence,
  listContacts, updateContactStatus, deleteContact, listAffiliatesAdmin,
  listPayments, listEmails, sendManualEmail, listAudit, cmsGet, cmsPut,
  adminListCountries, adminToggleCountry, adminGetPricingExtras, adminSavePricingExtras,
} from '../lib/adminApi';
import {
  LogOut, Search, Download, ArrowRight, RefreshCw, Loader2, FileText, MessagesSquare,
  Users, Wallet, Mail, Database, ScrollText, Home, Trash2, Send, CheckCircle2,
  Clock, AlertCircle, XCircle, ChevronLeft, ChevronRight, ShieldCheck, Undo2,
  Ban, TrendingDown, Percent, Calendar, Globe, Archive, ExternalLink, MailCheck, MailOpen,
  FileCheck, ClipboardList, Plus, X, FileClock, Upload, Receipt, FileSignature, BookMarked, Paperclip, Package, Unlock, PenSquare, FileWarning, Eye, SlidersHorizontal, FilePlus,
} from 'lucide-react';
import { Switch } from '../components/ui/switch';

const STATUS_STYLES = {
  received: { label: 'Received', color: 'bg-neutral-100 text-neutral-800', icon: Clock },
  in_review: { label: 'In Review', color: 'bg-amber-100 text-amber-900', icon: RefreshCw },
  submitted: { label: 'Submitted to ICA', color: 'bg-sky-100 text-sky-900', icon: Send },
  approved: { label: 'Approved', color: 'bg-emerald-100 text-emerald-900', icon: CheckCircle2 },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-900', icon: XCircle },
};

function StatusPill({ status }) {
  const s = STATUS_STYLES[status] || { label: status || 'unknown', color: 'bg-neutral-100 text-neutral-700', icon: AlertCircle };
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium ${s.color}`}>
      <Icon size={11} /> {s.label}
    </span>
  );
}

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return String(iso); }
}

export default function AdminDashboard() {
  const nav = useNavigate();
  const { toast } = useToast();
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState('overview');
  const [detailRef, setDetailRef] = useState(null);
  // Bumped whenever a detail-dialog action deletes/archives an
  // application, so the currently visible list refetches without
  // requiring the admin to click Refresh manually.
  const [listReloadKey, setListReloadKey] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const res = await adminMe();
        if (res.admin.mustChangePassword) { nav('/admin/change-password'); return; }
        setMe(res.admin);
      } catch { clearAdminToken(); nav('/admin/login'); }
    })();
  }, [nav]);

  const logout = () => { clearAdminToken(); nav('/admin/login'); };

  if (!me) return <div className="min-h-screen grid place-items-center text-neutral-500"><Loader2 className="animate-spin" /></div>;

  const NAV = [
    { id: 'overview', label: 'Overview', icon: Home },
    { id: 'applications', label: 'Applications', icon: FileText },
    { id: 'customers', label: 'Customers', icon: Users },
    { id: 'contacts', label: 'Contacts', icon: MessagesSquare },
    { id: 'affiliates', label: 'Affiliates', icon: Users },
    { id: 'payments', label: 'Payments', icon: Wallet },
    { id: 'emails', label: 'Emails', icon: Mail },
    { id: 'countries', label: 'Countries', icon: Globe },
    { id: 'cms', label: 'CMS', icon: Database },
    { id: 'audit', label: 'Audit log', icon: ScrollText },
  ];

  return (
    <div className="min-h-screen bg-neutral-50 flex">
      {/* Sidebar */}
      <aside className="w-60 shrink-0 bg-neutral-950 text-neutral-300 sticky top-0 h-screen flex flex-col">
        <div className="p-5 border-b border-neutral-900">
          <div className="flex items-center gap-2.5">
            <div className="relative w-9 h-9 rounded-lg bg-white text-teal-900 grid place-items-center">
              <span className="font-serif text-lg">W</span>
              <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-red-500 border-2 border-neutral-950"></span>
            </div>
            <div className="leading-tight">
              <div className="font-display text-white">Waan Admin</div>
              <div className="text-[10px] uppercase tracking-[0.14em] text-neutral-500">Console</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto p-3 space-y-0.5">
          {NAV.map((n) => (
            <button key={n.id} onClick={() => setTab(n.id)}
              className={`w-full text-left px-3 py-2 rounded-md text-sm flex items-center gap-2 ${tab === n.id ? 'bg-white/10 text-white' : 'hover:bg-white/5 text-neutral-300'}`}>
              <n.icon size={15} /> {n.label}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-neutral-900 text-xs text-neutral-400">
          <div>Signed in as</div>
          <div className="text-white truncate">{me.email}</div>
          <Button variant="ghost" onClick={logout} className="w-full mt-3 justify-start text-neutral-300 hover:text-white hover:bg-white/10"><LogOut size={14} className="mr-1.5" /> Log out</Button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <header className="sticky top-0 z-10 bg-white border-b border-neutral-200 px-6 py-3 flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-[0.14em] text-teal-800 font-medium">Waan Travel</div>
            <div className="font-display text-xl text-neutral-900">{NAV.find((n) => n.id === tab)?.label}</div>
          </div>
          <div className="flex items-center gap-2 text-xs text-neutral-500">
            <ShieldCheck size={12} className="text-teal-800" /> Console v1.0
          </div>
        </header>

        <div className="p-6">
          {tab === 'overview' && <OverviewTab onOpen={setDetailRef} />}
          {tab === 'applications' && <ApplicationsTab onOpen={setDetailRef} reloadKey={listReloadKey} />}
          {tab === 'customers' && <CustomersTab onOpen={setDetailRef} reloadKey={listReloadKey} />}
          {tab === 'contacts' && <ContactsTab />}
          {tab === 'affiliates' && <AffiliatesTab />}
          {tab === 'payments' && <PaymentsTab onOpen={setDetailRef} />}
          {tab === 'emails' && <EmailsTab />}
          {tab === 'countries' && <CountriesTab />}
          {tab === 'cms' && <CmsTab />}
          {tab === 'audit' && <AuditTab />}
        </div>
      </main>

      {detailRef && (
        <ApplicationDetail
          refId={detailRef}
          onClose={() => setDetailRef(null)}
          onDeleted={() => {
            // Bump the reload key so ApplicationsTab (and any other
            // active tab list) refetches after a delete from the detail
            // dialog. The dialog is already closing itself.
            setListReloadKey((k) => k + 1);
          }}
        />
      )}
    </div>
  );
}

/* ================================================================ */
/* OVERVIEW                                                          */
/* ================================================================ */
function OverviewTab({ onOpen }) {
  const [stats, setStats] = useState(null);
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const [s, a] = await Promise.all([adminStats(), listApplications({ page: 1, pageSize: 5 })]);
        setStats(s); setApps(a.items || []);
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <SkeletonBar />;
  if (!stats) return null;

  const cards = [
    { label: 'Total applications', value: stats.applications.total, sub: `${stats.applications.thisWeek} this week`, icon: FileText },
    { label: 'Pending review', value: stats.applications.pending, sub: 'received / in review / submitted', icon: Clock },
    { label: 'Approved', value: stats.applications.approved, sub: `${stats.applications.rejected} rejected`, icon: CheckCircle2 },
    { label: 'Revenue (USD)', value: `$${(stats.revenue.total || 0).toLocaleString()}`, sub: `$${(stats.revenue.thisWeek || 0).toLocaleString()} this week`, icon: Wallet },
    { label: 'Contact enquiries', value: stats.contacts, sub: 'all time', icon: MessagesSquare },
    { label: 'Affiliate partners', value: stats.affiliates, sub: 'total signups', icon: Users },
    { label: 'Emails sent', value: stats.emailsSent, sub: 'via Resend', icon: Mail },
  ];
  const total = Math.max(1, stats.applications.total);
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {cards.map((c, i) => (
          <div key={i} className="rounded-2xl border border-neutral-200 bg-white p-5">
            <div className="flex items-center gap-2 text-neutral-500 text-xs"><c.icon size={13} /> {c.label}</div>
            <div className="font-display text-3xl text-neutral-900 mt-2">{c.value}</div>
            <div className="text-xs text-neutral-500 mt-1">{c.sub}</div>
          </div>
        ))}
      </div>

      <RefundAnalyticsCard />

      <div className="rounded-2xl border border-neutral-200 bg-white p-6">
        <div className="font-display text-lg text-neutral-900">Status breakdown</div>
        <div className="mt-4 space-y-3">
          {Object.entries(stats.applications.byStatus).map(([k, v]) => (
            <div key={k}>
              <div className="flex items-center justify-between text-sm">
                <StatusPill status={k} />
                <span className="text-neutral-700">{v} ({Math.round((v / total) * 100)}%)</span>
              </div>
              <div className="mt-1 h-2 rounded-full bg-neutral-100 overflow-hidden">
                <div className="h-full bg-teal-700" style={{ width: `${(v / total) * 100}%` }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-neutral-200 bg-white p-6">
        <div className="flex items-center justify-between">
          <div className="font-display text-lg text-neutral-900">Latest applications</div>
        </div>
        <ApplicationsRows items={apps} onOpen={onOpen} />
      </div>
    </div>
  );
}

/* ================================================================ */
/* REFUND ANALYTICS CARD                                              */
/* ================================================================ */
function RefundAnalyticsCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try { setData(await getRefundAnalytics()); }
      catch { setData(null); }
      finally { setLoading(false); }
    })();
  }, []);
  if (loading) return (
    <div className="rounded-2xl border border-neutral-200 bg-white p-6" data-testid="refund-analytics-loading">
      <div className="animate-pulse h-6 bg-neutral-100 rounded w-48" />
    </div>
  );
  if (!data) return null;
  const monthName = new Date().toLocaleString('en-US', { month: 'long' });
  const tiles = [
    { label: `Refunded (${monthName})`, value: `$${data.total_refunded_mtd.toLocaleString()}`, sub: `${data.refund_count_mtd} refund${data.refund_count_mtd === 1 ? '' : 's'}`, icon: Undo2, tone: 'red' },
    { label: 'Refund rate', value: `${data.refund_rate_pct}%`, sub: `${data.refund_count_mtd} of ${data.paid_count_mtd} paid`, icon: Percent, tone: 'amber' },
    { label: `Net revenue (${monthName})`, value: `$${data.net_revenue_mtd.toLocaleString()}`, sub: `Gross $${data.gross_revenue_mtd.toLocaleString()}`, icon: TrendingDown, tone: 'emerald' },
  ];
  const toneClass = {
    red: 'text-red-700 bg-red-50 border-red-100',
    amber: 'text-amber-700 bg-amber-50 border-amber-100',
    emerald: 'text-emerald-700 bg-emerald-50 border-emerald-100',
  };
  return (
    <div className="rounded-2xl border border-neutral-200 bg-white p-6" data-testid="refund-analytics-card">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-display text-lg text-neutral-900">Refund analytics</div>
          <div className="text-xs text-neutral-500 mt-0.5">Month-to-date · {monthName} {new Date().getFullYear()}</div>
        </div>
      </div>
      <div className="mt-4 grid md:grid-cols-3 gap-3">
        {tiles.map((t, i) => (
          <div key={i} className={`rounded-xl border p-4 ${toneClass[t.tone]}`} data-testid={`refund-analytics-tile-${i}`}>
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest opacity-80"><t.icon size={12} /> {t.label}</div>
            <div className="font-display text-2xl mt-2">{t.value}</div>
            <div className="text-xs opacity-70 mt-1">{t.sub}</div>
          </div>
        ))}
      </div>
      {data.top_reasons.length > 0 && (
        <div className="mt-5" data-testid="refund-top-reasons">
          <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-2">Top refund reasons</div>
          <div className="space-y-1.5">
            {data.top_reasons.map((r, i) => (
              <div key={i} className="flex items-center justify-between text-sm border border-neutral-100 rounded-md px-3 py-2 bg-neutral-50">
                <span className="text-neutral-800 truncate max-w-md" title={r.reason}>{r.reason}</span>
                <span className="text-neutral-500 text-xs">{r.count}×</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ================================================================ */
/* REFUND POLICY DIALOG                                               */
/* ================================================================ */
function RefundPolicyDialog({ open, onClose, refId, provider, onDone }) {
  const { toast } = useToast();
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reason, setReason] = useState('Customer requested cancellation');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    (async () => {
      try { setPolicy(await getRefundPolicy(refId)); }
      catch (e) { toast({ title: 'Failed to load refund policy', description: e?.response?.data?.detail || e.message }); onClose(); }
      finally { setLoading(false); }
    })();
    // reset form each open
    setReason('Customer requested cancellation'); setNotes('');
    // eslint-disable-next-line
  }, [open, refId]);

  const confirm = async () => {
    if (!policy?.allowed) return;
    setBusy(true);
    try {
      const res = await refundApplication(refId, { amount: policy.refund_cents, reason, admin_notes: notes });
      toast({ title: 'Refund submitted', description: `${(provider || 'PayPal').toUpperCase()} is processing $${(res.refund.amount || 0).toFixed(2)} (${policy.refund_percentage}%).` });
      onDone && onDone();
      onClose();
    } catch (e) {
      toast({ title: 'Refund failed', description: e?.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="refund-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Undo2 size={16} /> Refund application</DialogTitle>
          <DialogDescription>Per the VisaSG Refund Policy, the amount is auto-calculated from the current application status.</DialogDescription>
        </DialogHeader>
        {loading || !policy ? (
          <div className="py-8 text-center text-sm text-neutral-500">Loading policy…</div>
        ) : !policy.allowed ? (
          <div className="space-y-4">
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800" data-testid="refund-blocked-message">
              <div className="flex items-start gap-2">
                <Ban size={16} className="mt-0.5 shrink-0" />
                <span>{policy.message}</span>
              </div>
            </div>
            <div className="flex justify-end">
              <Button variant="outline" onClick={onClose} data-testid="refund-blocked-close">Close</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-xl border border-neutral-200 divide-y divide-neutral-100" data-testid="refund-dialog-summary">
              <Row label="Application" value={<span className="font-mono text-sm">{refId}</span>} />
              <Row label="Status" value={<StatusPill status={policy.status} />} />
              <Row label="Original Payment" value={<span className="text-neutral-900">{policy.currency} {policy.original_amount.toFixed(2)}</span>} />
              <Row label="Refund Policy" value={<span className="font-medium text-neutral-900" data-testid="refund-policy-pct">{policy.refund_percentage}%</span>} />
              <Row label="Refund Amount" value={<span className="font-display text-lg text-emerald-700" data-testid="refund-calculated-amount">{policy.currency} {policy.refund_amount.toFixed(2)}</span>} />
            </div>
            <div>
              <Label className="text-xs">Refund reason</Label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger className="h-10 mt-1.5" data-testid="refund-reason-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Customer requested cancellation">Customer requested cancellation</SelectItem>
                  <SelectItem value="Duplicate application">Duplicate application</SelectItem>
                  <SelectItem value="Ineligible - policy exception">Ineligible - policy exception</SelectItem>
                  <SelectItem value="Payment error">Payment error</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Admin notes (internal)</Label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="mt-1.5" placeholder="Add context for the audit log…" data-testid="refund-admin-notes" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
              <Button onClick={confirm} disabled={busy} className="bg-red-600 hover:bg-red-700 text-white" data-testid="refund-confirm">
                {busy ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Undo2 size={14} className="mr-1.5" />}
                Confirm Refund
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between px-3 py-2.5">
      <span className="text-xs uppercase tracking-widest text-neutral-500">{label}</span>
      <span className="text-sm">{value}</span>
    </div>
  );
}

/* ================================================================ */
/* APPLICATIONS                                                      */
/* ================================================================ */
function ApplicationsTab({ onOpen, reloadKey }) {
  const { toast } = useToast();
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [sort, setSort] = useState('createdAt');
  const [order, setOrder] = useState('desc');
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);

  const fetch = async () => {
    setLoading(true);
    try {
      const res = await listApplications({ q: q || undefined, status: status || undefined, sort, order, page, pageSize, include_archived: showArchived ? 'true' : undefined });
      setData({ items: res.items, total: res.total });
    } finally { setLoading(false); }
  };
  useEffect(() => { fetch(); /* eslint-disable-next-line */ }, [status, sort, order, page, showArchived, reloadKey]);

  const doExport = async () => {
    try { await downloadApplicationsCsv(); toast({ title: 'CSV downloaded' }); }
    catch { toast({ title: 'Export failed' }); }
  };

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / pageSize));
  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-neutral-200 bg-white p-4 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[220px]">
          <Label className="text-xs text-neutral-500">Search</Label>
          <div className="relative mt-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <Input className="pl-9 h-10" placeholder="Ref, name, email, passport…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (setPage(1), fetch())} />
          </div>
        </div>
        <div>
          <Label className="text-xs text-neutral-500">Status</Label>
          <Select value={status || 'all'} onValueChange={(v) => { setStatus(v === 'all' ? '' : v); setPage(1); }}>
            <SelectTrigger className="h-10 w-44 mt-1"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {Object.keys(STATUS_STYLES).map((k) => <SelectItem key={k} value={k}>{STATUS_STYLES[k].label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-neutral-500">Sort by</Label>
          <Select value={`${sort}-${order}`} onValueChange={(v) => { const [s, o] = v.split('-'); setSort(s); setOrder(o); setPage(1); }}>
            <SelectTrigger className="h-10 w-52 mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="createdAt-desc">Newest first</SelectItem>
              <SelectItem value="createdAt-asc">Oldest first</SelectItem>
              <SelectItem value="total-desc">Highest value</SelectItem>
              <SelectItem value="total-asc">Lowest value</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2 ml-auto flex-wrap">
          <label className="inline-flex items-center gap-2 text-sm text-neutral-700 cursor-pointer mr-1" data-testid="apps-show-archived-toggle">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => { setShowArchived(e.target.checked); setPage(1); }}
              className="rounded border-neutral-300 text-teal-800 focus:ring-teal-800"
              data-testid="apps-show-archived-checkbox"
            />
            Show archived
          </label>
          <Button variant="outline" onClick={() => { setPage(1); fetch(); }}><RefreshCw size={14} className="mr-1.5" /> Refresh</Button>
          <Button variant="outline" onClick={() => setBulkOpen(true)} className="border-red-200 text-red-700 hover:bg-red-50" data-testid="apps-bulk-delete-btn">
            <Trash2 size={14} className="mr-1.5" /> Bulk delete
          </Button>
          <Button onClick={doExport} className="bg-teal-800 hover:bg-teal-900 text-white"><Download size={14} className="mr-1.5" /> Export CSV</Button>
        </div>
      </div>

      <div className="rounded-2xl border border-neutral-200 bg-white overflow-hidden">
        {loading ? <SkeletonBar /> : <ApplicationsRows items={data.items} onOpen={onOpen} onChanged={fetch} />}
        <div className="flex items-center justify-between border-t border-neutral-100 px-4 py-3 text-sm">
          <div className="text-neutral-500">Showing {(data.items?.length || 0)} of {data.total}</div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}><ChevronLeft size={14} /></Button>
            <span className="text-neutral-700">Page {page} / {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}><ChevronRight size={14} /></Button>
          </div>
        </div>
      </div>
      {bulkOpen && (
        <BulkDeleteDialog
          onClose={() => setBulkOpen(false)}
          onDone={() => { setBulkOpen(false); setPage(1); fetch(); }}
        />
      )}
    </div>
  );
}

/* ================================================================ */
/* BULK DELETE                                                       */
/* ================================================================ */
// One-off cleanup dialog for known test-application refs. Runs the
// backend preview first so the operator sees exactly which refs are
// deletable / blocked / not-found before committing. A blocked ref is
// only deleted when the operator explicitly opts in with `force=true`
// AND certifies the test-only checkbox — this preserves the
// safe-by-default single-delete rule while giving a controlled escape
// hatch for the batch we ship as a one-off.
function BulkDeleteDialog({ onClose, onDone }) {
  const { toast } = useToast();
  const [refsText, setRefsText] = useState('');
  const [preview, setPreview] = useState(null);
  const [force, setForce] = useState(false);
  const [certified, setCertified] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  // Parse refs: split on newlines, commas, spaces; strip and uppercase.
  const parsedRefs = Array.from(new Set(
    refsText.split(/[\s,]+/).map((r) => r.trim().toUpperCase()).filter(Boolean)
  ));
  const runPreview = async () => {
    if (parsedRefs.length === 0) return;
    setBusy(true);
    try {
      const res = await bulkDeleteApplicationsPreview(parsedRefs, force);
      setPreview(res);
    } catch (e) {
      toast({ title: 'Preview failed', description: e?.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };
  const runDelete = async () => {
    if (confirmText.trim() !== 'DELETE') {
      toast({ title: 'Type DELETE to confirm', description: 'Case-sensitive.' });
      return;
    }
    if (force && !certified) {
      toast({ title: 'Certification required', description: 'Tick the test-record certification to force-delete protected rows.' });
      return;
    }
    setBusy(true);
    try {
      const res = await bulkDeleteApplications(parsedRefs, force);
      toast({
        title: `Deleted ${res.summary.deleted}/${parsedRefs.length}`,
        description: `${res.summary.blocked} blocked, ${res.summary.not_found} not found${res.summary.forced ? `, ${res.summary.forced} forced` : ''}.`,
      });
      setPreview({ summary: {
        deletable: res.summary.deleted,
        blocked: res.summary.blocked,
        not_found: res.summary.not_found,
      }, results: res.results, isFinal: true });
      onDone();
    } catch (e) {
      toast({ title: 'Bulk delete failed', description: e?.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };
  const stats = preview?.summary;
  const canPreview = !busy && parsedRefs.length > 0;
  const wouldDelete = stats ? ((stats.deletable || stats.deleted || 0)) : 0;
  const canDelete = preview && !busy && confirmText.trim() === 'DELETE' && (!force || certified) && wouldDelete > 0;
  return (
    <Dialog open onOpenChange={(v) => { if (!v && !busy) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="bulk-delete-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Trash2 size={16} className="text-red-600" /> Bulk delete test applications</DialogTitle>
          <DialogDescription>Paste the WT-XXXXXX references you want to permanently remove — one per line, or comma/space separated. Preview first, then confirm.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs text-neutral-500">References ({parsedRefs.length} unique)</Label>
            <textarea
              className="mt-1 w-full h-32 rounded-md border border-neutral-200 p-3 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-teal-700"
              placeholder={"WT-XXXXXX\nWT-YYYYYY\n…"}
              value={refsText}
              onChange={(e) => { setRefsText(e.target.value); setPreview(null); }}
              data-testid="bulk-delete-refs"
            />
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <label className="inline-flex items-center gap-2 text-sm cursor-pointer" data-testid="bulk-delete-force-toggle">
              <input
                type="checkbox"
                checked={force}
                onChange={(e) => { setForce(e.target.checked); setPreview(null); }}
                className="rounded border-neutral-300 text-teal-800 focus:ring-teal-800"
                data-testid="bulk-delete-force-checkbox"
              />
              Force-delete paid/processing rows (rare)
            </label>
            <Button
              onClick={runPreview}
              disabled={!canPreview}
              variant="outline"
              className="ml-auto"
              data-testid="bulk-delete-preview-btn"
            >
              {busy && !preview ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <Search size={13} className="mr-1.5" />}
              Preview
            </Button>
          </div>
          {preview && (
            <div className="space-y-3">
              <div className="rounded-lg border border-neutral-200 p-3 flex flex-wrap gap-2 text-xs" data-testid="bulk-delete-summary">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">
                  <CheckCircle2 size={11} /> {preview.isFinal ? 'Deleted' : 'Deletable'}: {stats.deletable || stats.deleted || 0}
                </span>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                  <Ban size={11} /> Blocked: {stats.blocked || 0}
                </span>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-neutral-100 text-neutral-600">
                  <XCircle size={11} /> Not found: {stats.not_found || 0}
                </span>
                {stats.forced ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-800">
                    <AlertCircle size={11} /> Forced: {stats.forced}
                  </span>
                ) : null}
              </div>
              <div className="rounded-lg border border-neutral-200 max-h-64 overflow-y-auto text-sm">
                <table className="w-full">
                  <thead className="text-[11px] uppercase tracking-wider text-neutral-500 bg-neutral-50 sticky top-0">
                    <tr>
                      <th className="text-left p-2 font-medium">Reference</th>
                      <th className="text-left p-2 font-medium">Status</th>
                      <th className="text-left p-2 font-medium">Payment</th>
                      <th className="text-left p-2 font-medium">Files</th>
                      <th className="text-left p-2 font-medium">Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.results.map((r) => (
                      <tr key={r.ref} className="border-t border-neutral-100">
                        <td className="p-2 font-mono text-xs">{r.ref}</td>
                        <td className="p-2 text-xs">{r.currentStatus || '—'}</td>
                        <td className="p-2 text-xs">{r.paymentStatus || '—'}</td>
                        <td className="p-2 text-xs">{r.filesCount ?? (r.filesDeleted ?? '—')}</td>
                        <td className="p-2 text-xs">
                          {r.status === 'deletable' && (
                            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full ${r.willForce ? 'bg-red-100 text-red-800' : 'bg-emerald-100 text-emerald-800'}`}>
                              <CheckCircle2 size={10} /> {r.willForce ? 'Force-deletable' : 'Deletable'}
                            </span>
                          )}
                          {r.status === 'deleted' && (
                            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full ${r.wasForced ? 'bg-red-100 text-red-800' : 'bg-emerald-100 text-emerald-800'}`}>
                              <CheckCircle2 size={10} /> {r.wasForced ? 'Force-deleted' : 'Deleted'}
                            </span>
                          )}
                          {r.status === 'blocked' && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800" title={r.reason}>
                              <Ban size={10} /> Blocked
                            </span>
                          )}
                          {r.status === 'not_found' && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-neutral-100 text-neutral-600">
                              <XCircle size={10} /> Not found
                            </span>
                          )}
                          {r.status === 'error' && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-red-100 text-red-800" title={r.error}>
                              <AlertCircle size={10} /> Error
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {!preview.isFinal && (
                <>
                  {force && (
                    <label className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-900 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={certified}
                        onChange={(e) => setCertified(e.target.checked)}
                        className="mt-0.5 rounded border-red-300 text-red-700 focus:ring-red-600"
                        data-testid="bulk-delete-certify-checkbox"
                      />
                      <span>
                        I certify these applications are development / test data and that permanent deletion will not compromise any real customer, payment or audit history. Force-delete will bypass the paid/processing protection for {stats.blocked || 0} row{(stats.blocked || 0) === 1 ? '' : 's'}.
                      </span>
                    </label>
                  )}
                  <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-800 flex items-start gap-2">
                    <AlertCircle size={14} className="mt-0.5 shrink-0" />
                    <span>This action cannot be undone. {wouldDelete} application{wouldDelete === 1 ? '' : 's'} plus their files and receipts will be permanently deleted.</span>
                  </div>
                  <div>
                    <Label className="text-xs">Type <span className="font-mono font-semibold">DELETE</span> to confirm</Label>
                    <Input
                      value={confirmText}
                      onChange={(e) => setConfirmText(e.target.value)}
                      className="h-10 mt-1.5 font-mono"
                      placeholder="DELETE"
                      data-testid="bulk-delete-confirm-input"
                    />
                  </div>
                </>
              )}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} disabled={busy}>{preview?.isFinal ? 'Close' : 'Cancel'}</Button>
            {preview && !preview.isFinal && (
              <Button
                onClick={runDelete}
                disabled={!canDelete}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="bulk-delete-confirm-btn"
              >
                {busy ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Trash2 size={14} className="mr-1.5" />}
                Delete {wouldDelete} application{wouldDelete === 1 ? '' : 's'}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ApplicationsRows({ items, onOpen, onChanged }) {
  const { toast } = useToast();
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [archiveTarget, setArchiveTarget] = useState(null);
  if (!items?.length) return <div className="text-center text-sm text-neutral-500 py-10">No applications yet.</div>;
  const isTestDeletable = (it) => (
    (it.status === 'pending_payment') && ((it.payment?.status || '').toLowerCase() !== 'succeeded')
  );
  return (
    <div>
      <div className="hidden md:grid grid-cols-12 bg-neutral-50 border-b border-neutral-200 text-[11px] uppercase tracking-wider text-neutral-500">
        <div className="col-span-2 p-3">Ref</div>
        <div className="col-span-3 p-3">Applicant</div>
        <div className="col-span-2 p-3">Nationality</div>
        <div className="col-span-1 p-3">Total</div>
        <div className="col-span-2 p-3">Status</div>
        <div className="col-span-2 p-3 text-right">Actions</div>
      </div>
      {items.map((it) => {
        const testDeletable = isTestDeletable(it);
        return (
          <div key={it.ref} className="grid grid-cols-2 md:grid-cols-12 items-center border-b last:border-b-0 border-neutral-100 hover:bg-teal-50/30 transition-colors">
            <button onClick={() => onOpen(it.ref)} className="col-span-2 md:col-span-2 p-3 text-left">
              <div className="font-mono text-sm text-neutral-900">{it.ref}</div>
              <div className="text-[11px] text-neutral-500">{fmtDate(it.createdAt)}</div>
              {it.archived && <div className="text-[10px] mt-0.5 text-neutral-500 inline-flex items-center gap-1"><Archive size={10} /> Archived</div>}
            </button>
            <button onClick={() => onOpen(it.ref)} className="col-span-2 md:col-span-3 p-3 text-left">
              <div className="text-sm font-medium text-neutral-900">{it.data?.fullName}</div>
              <div className="text-[11px] text-neutral-500 truncate">{it.data?.email}</div>
            </button>
            <div className="md:col-span-2 p-3 text-sm text-neutral-700 capitalize">{it.data?.nationality}</div>
            <div className="md:col-span-1 p-3 text-sm font-medium text-neutral-900">${it.total}</div>
            <div className="md:col-span-2 p-3"><StatusPill status={it.status} /></div>
            <div className="md:col-span-2 p-3 flex items-center justify-end gap-1.5">
              {testDeletable ? (
                <button
                  onClick={(e) => { e.stopPropagation(); setDeleteTarget(it); }}
                  className="inline-flex items-center gap-1 h-8 px-2.5 rounded-md border border-red-200 bg-red-50 text-red-700 text-xs font-medium hover:bg-red-100"
                  title="Delete test application"
                  data-testid={`admin-app-delete-${it.ref}`}
                ><Trash2 size={13} /> <span className="hidden lg:inline">Delete</span></button>
              ) : (
                !it.archived && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setArchiveTarget(it); }}
                    className="inline-flex items-center gap-1 h-8 px-2.5 rounded-md border border-teal-200 bg-teal-50 text-teal-800 text-xs font-medium hover:bg-teal-100"
                    title="Archive application"
                    data-testid={`admin-app-archive-${it.ref}`}
                  ><Archive size={13} /> <span className="hidden lg:inline">Archive</span></button>
                )
              )}
              <button onClick={() => onOpen(it.ref)} className="p-1.5 rounded-md text-neutral-400 hover:text-teal-800" title="Open">
                <ArrowRight size={14} />
              </button>
            </div>
          </div>
        );
      })}
      {deleteTarget && (
        <ApplicationDeleteDialog
          app={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDeleted={() => { setDeleteTarget(null); toast({ title: `Deleted ${deleteTarget.ref}` }); onChanged && onChanged(); }}
        />
      )}
      {archiveTarget && (
        <ApplicationArchiveDialog
          app={archiveTarget}
          onClose={() => setArchiveTarget(null)}
          onArchived={() => { setArchiveTarget(null); toast({ title: `Archived ${archiveTarget.ref}` }); onChanged && onChanged(); }}
        />
      )}
    </div>
  );
}

/* Application-delete dialog with "Type WT-XXXXXX to confirm" pattern.
   Only shown for status==='pending_payment' && payment != succeeded. */
function ApplicationDeleteDialog({ app, onClose, onDeleted }) {
  const { toast } = useToast();
  const [impact, setImpact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const r = await applicationDeletionImpact(app.ref);
        setImpact(r.impact);
      } catch (e) {
        toast({ title: 'Failed to load preview', description: e?.response?.data?.detail || e.message });
        onClose();
      } finally { setLoading(false); }
    })();
  }, [app.ref]);
  const confirm = async () => {
    if (confirmText.trim().toUpperCase() !== app.ref.toUpperCase()) {
      toast({ title: 'Type the exact reference to confirm', description: `Please type ${app.ref}.` });
      return;
    }
    setBusy(true);
    try {
      await deleteApplication(app.ref);
      onDeleted();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast({
        title: 'Cannot delete application',
        description: typeof detail === 'object' ? detail.message : (detail || e.message),
      });
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="application-delete-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Trash2 size={16} className="text-red-600" /> Delete test application</DialogTitle>
          <DialogDescription><span className="font-mono">{app.ref}</span></DialogDescription>
        </DialogHeader>
        {loading || !impact ? (
          <div className="py-8 text-center text-sm text-neutral-500">Loading impact preview…</div>
        ) : !impact.eligibleForDeletion ? (
          <div className="space-y-4">
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
              <div className="flex items-start gap-2">
                <Ban size={16} className="mt-0.5 shrink-0" />
                <span>This application cannot be permanently deleted ({impact.blockedReason}). Please use Archive instead.</span>
              </div>
            </div>
            <div className="flex justify-end"><Button variant="outline" onClick={onClose}>Close</Button></div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-800 flex items-start gap-2">
              <AlertCircle size={14} className="mt-0.5 shrink-0" />
              <span>This action cannot be undone.</span>
            </div>
            <div className="rounded-lg border border-neutral-200 divide-y divide-neutral-100 text-sm">
              <Row label="Reference" value={<span className="font-mono">{impact.ref}</span>} />
              <Row label="Status" value={<StatusPill status={impact.status} />} />
              <Row label="Payment" value={impact.paymentStatus || 'None'} />
              <Row label="Files" value={impact.filesCount} />
              <Row label="Receipt" value={impact.hasReceipt ? 'Present' : 'None'} />
              <Row label="Pending uploads" value={impact.pendingUploads} />
            </div>
            <div>
              <Label className="text-xs">Type <span className="font-mono font-semibold">{app.ref}</span> to confirm</Label>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                className="h-10 mt-1.5 font-mono"
                placeholder={app.ref}
                data-testid="application-delete-confirm-input"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
              <Button
                onClick={confirm}
                disabled={busy || confirmText.trim().toUpperCase() !== app.ref.toUpperCase()}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="application-delete-confirm"
              >
                {busy ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Trash2 size={14} className="mr-1.5" />}
                Delete permanently
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* Application-archive dialog. Non-destructive so a simple confirm is fine. */
function ApplicationArchiveDialog({ app, onClose, onArchived }) {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const confirm = async () => {
    setBusy(true);
    try {
      await archiveApplication(app.ref);
      onArchived();
    } catch (e) {
      toast({ title: 'Archive failed', description: e?.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="application-archive-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Archive size={16} /> Archive application</DialogTitle>
          <DialogDescription><span className="font-mono">{app.ref}</span></DialogDescription>
        </DialogHeader>
        <div className="rounded-lg border border-teal-200 bg-teal-50/70 p-3 text-xs text-teal-900 flex items-start gap-2">
          <Archive size={14} className="mt-0.5 shrink-0" />
          <span>Archived applications are hidden from the default list but remain fully queryable, refundable and downloadable. Nothing is deleted.</span>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={confirm} disabled={busy} className="bg-teal-800 hover:bg-teal-900 text-white" data-testid="application-archive-confirm">
            {busy ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Archive size={14} className="mr-1.5" />}
            Archive
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ================================================================ */
/* APPLICATION DETAIL (dialog)                                       */
/* ================================================================ */
function ApplicationDetail({ refId, onClose, onDeleted }) {
  const { toast } = useToast();
  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState('');
  const [statusForm, setStatusForm] = useState({ status: '', note: '', notifyTraveler: true });
  const [busy, setBusy] = useState(false);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [archiveDialog, setArchiveDialog] = useState(false);

  const fetch = async () => {
    setLoading(true);
    try {
      const res = await getApplication(refId);
      setApp(res.application);
      setStatusForm((f) => ({ ...f, status: res.application.status }));
    } finally { setLoading(false); }
  };
  useEffect(() => { fetch(); /* eslint-disable-next-line */ }, [refId]);

  const onStatus = async () => {
    if (!statusForm.status) return;
    setBusy(true);
    try {
      const res = await updateStatus(refId, statusForm.status, statusForm.note, statusForm.notifyTraveler);
      toast({ title: 'Status updated', description: res.emailNotified ? 'Applicant notified by email.' : 'Traveler was not notified.' });
      setStatusForm({ ...statusForm, note: '' });
      await fetch();
    } catch (e) { toast({ title: 'Update failed', description: e?.response?.data?.detail || 'Please try again.' }); }
    finally { setBusy(false); }
  };

  const onAddNote = async () => {
    if (!note.trim()) return;
    setBusy(true);
    try { await addNote(refId, note); setNote(''); await fetch(); toast({ title: 'Note added' }); }
    finally { setBusy(false); }
  };

  const dl = async (fileId, filename) => {
    try { await downloadFile(refId, fileId, filename); }
    catch { toast({ title: 'Download failed' }); }
  };

  return (
    <Dialog open={!!refId} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="application-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 pr-8 flex-wrap">
            <span className="font-mono">{refId}</span>
            {app?.status && <StatusPill status={app.status} />}
            {app?.archived && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-neutral-200 text-neutral-800">
                <Archive size={11} /> Archived
              </span>
            )}
          </DialogTitle>
          <DialogDescription>Full application details, files, status and internal notes.</DialogDescription>
        </DialogHeader>
        {/* Destructive actions live at the top of the detail dialog so
            admins can find them without scrolling. Deletion eligibility
            mirrors the list-view rule: only pending_payment + not-succeeded
            shows Delete, everything else shows Archive (or the row is
            already archived and offers Unarchive). */}
        {app && (() => {
          const testDeletable = app.status === 'pending_payment' && (app.payment?.status || '').toLowerCase() !== 'succeeded';
          return (
            <div className="flex items-center gap-2 flex-wrap -mt-2 pb-2 border-b border-neutral-100">
              {testDeletable && !app.archived && (
                <Button
                  variant="outline"
                  size="sm"
                  className="border-red-200 bg-red-50 text-red-700 hover:bg-red-100"
                  onClick={() => setDeleteDialog(true)}
                  data-testid="detail-delete-btn"
                ><Trash2 size={13} className="mr-1.5" /> Delete test application</Button>
              )}
              {!testDeletable && !app.archived && (
                <Button
                  variant="outline"
                  size="sm"
                  className="border-teal-200 bg-teal-50 text-teal-800 hover:bg-teal-100"
                  onClick={() => setArchiveDialog(true)}
                  data-testid="detail-archive-btn"
                ><Archive size={13} className="mr-1.5" /> Archive application</Button>
              )}
              {app.archived && (
                <Button
                  variant="outline"
                  size="sm"
                  className="border-neutral-200 bg-neutral-50 text-neutral-700 hover:bg-neutral-100"
                  onClick={async () => {
                    setBusy(true);
                    try {
                      await unarchiveApplication(refId);
                      toast({ title: 'Application unarchived' });
                      await fetch();
                    } catch (e) {
                      toast({ title: 'Unarchive failed', description: e?.response?.data?.detail || e.message });
                    } finally { setBusy(false); }
                  }}
                  disabled={busy}
                  data-testid="detail-unarchive-btn"
                ><Undo2 size={13} className="mr-1.5" /> Unarchive</Button>
              )}
            </div>
          );
        })()}
        {loading || !app ? <SkeletonBar /> : (
          <div className="space-y-5">
            <div className="grid md:grid-cols-3 gap-4">
              <div className="rounded-xl border border-neutral-200 p-4">
                <div className="text-[11px] uppercase tracking-widest text-neutral-500">Traveler</div>
                <div className="font-medium mt-1">{app.data?.fullName}</div>
                <div className="text-sm text-neutral-600">{app.data?.email}</div>
                <div className="text-sm text-neutral-600">{app.data?.mobile}</div>
              </div>
              <div className="rounded-xl border border-neutral-200 p-4">
                <div className="text-[11px] uppercase tracking-widest text-neutral-500">Visa</div>
                <div className="mt-1 text-sm">Nationality: <span className="font-medium">{app.data?.nationality}</span></div>
                <div className="text-sm">Type: <span className="font-medium">{app.data?.visaType}</span></div>
                <div className="text-sm">Arrival: <span className="font-medium">{app.data?.arrival}</span></div>
              </div>
              <div className="rounded-xl border border-neutral-200 p-4">
                <div className="text-[11px] uppercase tracking-widest text-neutral-500">Payment</div>
                <div className="font-display text-xl mt-1">${app.total}</div>
                <div className="text-[11px] text-neutral-500">{app.payment?.provider} · {app.payment?.status} · {app.payment?.mode}</div>
                {app.payment?.intent_id && (
                  <div className="text-[10px] font-mono text-neutral-400 mt-1 truncate" title={app.payment.intent_id}>Txn: {app.payment.intent_id}</div>
                )}
                <div className="mt-3 flex gap-2 flex-wrap">
                  <Button size="sm" variant="outline" disabled={!app.receipt} onClick={() => downloadReceipt(refId).catch(() => toast({ title: 'Download failed' }))}>
                    <Download size={14} className="mr-1" /> Receipt PDF
                  </Button>
                  <Button size="sm" variant="outline" onClick={async () => { try { await regenerateReceipt(refId); toast({ title: 'Receipt regenerated' }); await fetch(); } catch { toast({ title: 'Regenerate failed' }); } }}>
                    <RefreshCw size={14} className="mr-1" /> Regenerate
                  </Button>
                  {['airwallex', 'paypal'].includes(app.payment?.provider) && (app.payment?.status || '').toLowerCase() === 'succeeded' && (app.payment?.refund_status || '') !== 'refunded' && (() => {
                    const isBlocked = ['approved', 'rejected'].includes((app.status || '').toLowerCase());
                    return (
                      <Button
                        size="sm"
                        variant="outline"
                        className={isBlocked ? 'border-neutral-300 text-neutral-400 cursor-not-allowed' : 'border-red-300 text-red-700 hover:bg-red-50'}
                        disabled={busy || isBlocked}
                        data-testid="refund-button"
                        title={isBlocked ? `Non-refundable per VisaSG Refund Policy (application is ${app.status}).` : 'Open the refund policy dialog'}
                        onClick={() => !isBlocked && setRefundDialogOpen(true)}
                      >
                        <Undo2 size={14} className="mr-1" /> Refund…
                      </Button>
                    );
                  })()}
                  {app.payment?.refund_status && (
                    <span className="text-[11px] px-2 py-1 rounded-full bg-red-50 text-red-700 border border-red-200 uppercase tracking-widest" data-testid="refund-status-badge">
                      {app.payment.refund_status.replace('_', ' ')}
                    </span>
                  )}
                </div>
                {/* Non-refundable notice when application is approved / rejected */}
                {['airwallex', 'paypal'].includes(app.payment?.provider) && (app.payment?.status || '').toLowerCase() === 'succeeded' && ['approved', 'rejected'].includes((app.status || '').toLowerCase()) && (
                  <div className="mt-3 rounded-lg border border-neutral-200 bg-neutral-50 p-3 text-[11px] leading-relaxed text-neutral-700 flex items-start gap-2" data-testid="refund-non-refundable-notice">
                    <Ban size={13} className="mt-0.5 shrink-0 text-neutral-500" />
                    <span>
                      {(app.status || '').toLowerCase() === 'approved'
                        ? 'This application has already been approved and is non-refundable according to the VisaSG Refund Policy.'
                        : 'This application has received a final decision (Rejected) and is non-refundable according to the VisaSG Refund Policy.'}
                    </span>
                  </div>
                )}
                {Array.isArray(app.refunds) && app.refunds.length > 0 && (
                  <div className="mt-3 space-y-1.5" data-testid="refund-history">
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500">Refunds</div>
                    {app.refunds.map((r, i) => (
                      <div key={i} className="text-[11px] flex items-center justify-between rounded border border-neutral-200 bg-neutral-50 px-2.5 py-1.5">
                        <div className="flex items-center gap-2">
                          <span className="uppercase tracking-widest text-neutral-500">{r.provider || 'refund'}</span>
                          <span className="font-mono text-neutral-800 truncate max-w-[180px]" title={r.id}>{r.id}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-neutral-900">{r.currency} {typeof r.amount === 'number' ? r.amount.toFixed(2) : r.amount}</span>
                          <span className={`px-1.5 py-0.5 rounded ${(r.status || '').toUpperCase() === 'COMPLETED' || (r.status || '').toLowerCase() === 'succeeded' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>{r.status}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="rounded-xl border border-neutral-200 p-4">
              <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-2">Personal & passport</div>
              <KVGrid data={app.data} />
            </div>

            {/* ICA rectification — per-traveler card grid. Shown whenever the
                application has a travelers[] array (single or multi). Displays
                every new ICA field explicitly and falls back to
                "Not provided" for legacy applications where a field was never
                collected, so admins never misread a missing value as "No". */}
            {Array.isArray(app.data?.travelers) && app.data.travelers.length > 0 && (
              <div className="rounded-xl border border-neutral-200 p-4" data-testid="admin-ica-per-traveler">
                <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-3">
                  Applicants & declarations ({app.data.travelers.length})
                </div>
                <div className="space-y-4">
                  {app.data.travelers.map((tv, i) => (
                    <ApplicantIcaCard key={i} idx={i} tv={tv} />
                  ))}
                </div>
              </div>
            )}

            <div className="rounded-xl border border-neutral-200 p-4">
              <div className="flex items-center justify-between">
                <div className="text-[11px] uppercase tracking-widest text-neutral-500">Uploaded documents</div>
                <span className="text-xs text-neutral-500">{app.files?.length || 0} files</span>
              </div>
              <div className="mt-3 grid md:grid-cols-2 gap-2">
                {(app.files || []).map((f) => (
                  <div key={f.id} className="flex items-center justify-between rounded-lg border border-neutral-200 p-3">
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{f.filename}</div>
                      <div className="text-[11px] text-neutral-500 capitalize">{f.kind} · {(f.size / 1024).toFixed(0)} KB · {f.content_type}</div>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => dl(f.id, f.filename)}><Download size={14} className="mr-1" /> Download</Button>
                  </div>
                ))}
                {!(app.files || []).length && <div className="text-sm text-neutral-500">No files uploaded.</div>}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="rounded-xl border border-neutral-200 p-4">
                <div className="text-[11px] uppercase tracking-widest text-neutral-500">Update status</div>
                <div className="mt-3 space-y-3">
                  <Select value={statusForm.status} onValueChange={(v) => setStatusForm({ ...statusForm, status: v })}>
                    <SelectTrigger className="h-10"><SelectValue placeholder="Select status" /></SelectTrigger>
                    <SelectContent>
                      {Object.keys(STATUS_STYLES).map((k) => <SelectItem key={k} value={k}>{STATUS_STYLES[k].label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Textarea value={statusForm.note} onChange={(e) => setStatusForm({ ...statusForm, note: e.target.value })} placeholder="Optional message to include in the email" className="min-h-20" />
                  {['submitted', 'approved'].includes(statusForm.status) ? (
                    <div className="rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 text-[11px] text-emerald-800 flex items-start gap-2">
                      <CheckCircle2 size={12} className="mt-0.5 shrink-0" />
                      <span>The traveler will be auto-notified by email for <strong>{statusForm.status}</strong>. This cannot be disabled.</span>
                    </div>
                  ) : (
                    <label className="flex items-center gap-2 text-xs text-neutral-700">
                      <input type="checkbox" checked={statusForm.notifyTraveler} onChange={(e) => setStatusForm({ ...statusForm, notifyTraveler: e.target.checked })} />
                      Notify traveler by email
                    </label>
                  )}
                  <Button disabled={busy || !statusForm.status} onClick={onStatus} className="bg-teal-800 hover:bg-teal-900 text-white w-full">{busy ? (<><Loader2 size={14} className="mr-1.5 animate-spin" /> Working…</>) : 'Update status'}</Button>
                </div>
                {(app.statusHistory || []).length > 0 && (
                  <div className="mt-4 border-t border-neutral-100 pt-3">
                    <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-2">History</div>
                    <ul className="space-y-2 text-xs text-neutral-700">
                      {app.statusHistory.map((h, i) => (
                        <li key={i} className="flex flex-col">
                          <span>{fmtDate(h.at)} · <StatusPill status={h.from} /> → <StatusPill status={h.to} /> · by {h.by}</span>
                          {h.note && <span className="text-neutral-500 mt-0.5">"{h.note}"</span>}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <div className="rounded-xl border border-neutral-200 p-4">
                <div className="text-[11px] uppercase tracking-widest text-neutral-500">Internal notes</div>
                <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add an internal note (not visible to traveler)" className="min-h-20 mt-3" />
                <Button disabled={busy || !note.trim()} onClick={onAddNote} className="mt-2 bg-neutral-900 hover:bg-neutral-800 text-white w-full">{busy ? (<><Loader2 size={14} className="mr-1.5 animate-spin" /> Saving…</>) : 'Add note'}</Button>
                <ul className="mt-4 space-y-3 text-sm">
                  {(app.notes || []).map((n, i) => (
                    <li key={i} className="rounded-lg bg-neutral-50 border border-neutral-100 p-3">
                      <div className="text-neutral-800 whitespace-pre-wrap">{n.text}</div>
                      <div className="text-[11px] text-neutral-500 mt-1">{fmtDate(n.at)} · {n.by}</div>
                    </li>
                  ))}
                  {!(app.notes || []).length && <li className="text-sm text-neutral-500">No notes yet.</li>}
                </ul>
              </div>
            </div>

            {/* Travel Service Records / ICA Evidence (Phase 1) - full-width panel */}
            <EvidencePanel refId={refId} />

            <div className="flex justify-end pt-2">
              <Button variant="outline" onClick={onClose}>Close</Button>
            </div>
          </div>
        )}
      </DialogContent>
      <RefundPolicyDialog
        open={refundDialogOpen}
        onClose={() => setRefundDialogOpen(false)}
        refId={refId}
        provider={app?.payment?.provider}
        onDone={fetch}
      />
      {deleteDialog && app && (
        <ApplicationDeleteDialog
          app={app}
          onClose={() => setDeleteDialog(false)}
          onDeleted={() => {
            setDeleteDialog(false);
            toast({ title: `Deleted ${refId}` });
            if (onDeleted) onDeleted();
            onClose();
          }}
        />
      )}
      {archiveDialog && app && (
        <ApplicationArchiveDialog
          app={app}
          onClose={() => setArchiveDialog(false)}
          onArchived={() => {
            setArchiveDialog(false);
            toast({ title: `Archived ${refId}` });
            fetch();
          }}
        />
      )}
    </Dialog>
  );
}

function KVGrid({ data }) {
  const rows = Object.entries(data || {}).filter(([, v]) => v !== null && v !== '' && typeof v !== 'object');
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-sm">
      {rows.map(([k, v]) => (
        <div key={k} className="flex items-start gap-2">
          <span className="text-neutral-500 min-w-[150px] capitalize">{k.replace(/([A-Z])/g, ' $1').trim()}:</span>
          <span className="text-neutral-900 break-words">{String(v)}</span>
        </div>
      ))}
    </div>
  );
}

/* ================================================================ */
/* TRAVEL SERVICE RECORDS / ICA EVIDENCE (Phase 1)                   */
/* ================================================================ */
// Auto-populated from the application row (server-derived snapshot) and
// carries the editable evidence-layer fields the operator uses to build
// the itinerary + transaction record PDFs. The evidence record NEVER
// mutates the underlying application; edits go to `travel_service_records`.
const SERVICE_CATEGORIES = [
  'Singapore Visa Application Assistance',
  'Free & Easy Tour Package',
  'Flight Ticket Booking',
  'Hotel Booking',
];

/* NEW EvidencePanel (Phase 5) — Customer / ICA Supporting Documents.
 *
 * Compact document-driven UI:
 *   – Snapshot header (applicant + travel + payment)
 *   – DRAFT / FINALISED banner with Finalise / Reopen buttons
 *   – 7-row document list with [Preview] [Edit] [PDF] per row
 *   – Supporting Documents manager (customer-uploaded files)
 *
 * All edits go to the additive travel_service_records collection and NEVER
 * mutate the underlying application row.
 */
function EvidencePanel({ refId }) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [record, setRecord] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [supporting, setSupporting] = useState([]);
  const [finaliseBusy, setFinaliseBusy] = useState(false);
  // Per-doc variant state (transaction-record and service-agreement)
  const [txVariant, setTxVariant] = useState('historical');
  const [csaVariant, setCsaVariant] = useState('historical');
  // Preview + Edit dialog state
  const [previewDoc, setPreviewDoc] = useState(null);   // { slug, params, title, filename }
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [editorDoc, setEditorDoc] = useState(null);     // editor key (see EDITOR_MAP)
  // Pack options
  const [packBusy, setPackBusy] = useState(false);
  const [packOptions, setPackOptions] = useState({
    includeSupporting: true,
    includeItinerary: true,
    includeTransaction: true,
    includeBookingSummary: true,
    includeServiceAgreement: true,
    includeServiceConfirmationLetter: true,
    includePaymentReceipt: true,
  });
  const [packEditorOpen, setPackEditorOpen] = useState(false);

  const loadSupporting = async () => {
    try {
      const r = await listEvidenceSupporting(refId);
      setSupporting(r.documents || []);
    } catch (e) { /* silent — panel still works */ }
  };
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await getEvidence(refId);
        setRecord(r.record);
        setSnapshot(r.snapshot);
        await loadSupporting();
      } catch (e) {
        toast({ title: 'Failed to load evidence', description: e?.response?.data?.detail || e.message });
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refId]);

  const isPaid = !!snapshot?.payment?.isPaid;
  const isDraft = (record?.draftStatus || 'draft') === 'draft';
  const locked = !isDraft;

  const patch = (updater) => {
    setDirty(true);
    setRecord((r) => (typeof updater === 'function' ? updater(r) : { ...r, ...updater }));
  };

  const save = async () => {
    if (!record) return;
    setSaving(true);
    try {
      const r = await updateEvidence(refId, {
        serviceCategories: record.serviceCategories || [],
        adminNotes: record.adminNotes || '',
        itinerary: record.itinerary || { days: [] },
        flightDetails: record.flightDetails || '',
        hotelDetails: record.hotelDetails || '',
        tourActivity: record.tourActivity || '',
        transportation: record.transportation || '',
        evidenceNotes: record.evidenceNotes || '',
        serviceDescriptions: record.serviceDescriptions || {},
        supportingClassifications: record.supportingClassifications || {},
        bookings: record.bookings || [],
      });
      setRecord(r.record);
      setDirty(false);
      toast({ title: 'Working draft saved' });
      return true;
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || e.message });
      return false;
    } finally { setSaving(false); }
  };

  const onFinalise = async () => {
    if (dirty && !window.confirm('You have unsaved changes. Save them first? Click OK to save and finalise; Cancel to abort.')) return;
    setFinaliseBusy(true);
    try {
      if (dirty) { const ok = await save(); if (!ok) { setFinaliseBusy(false); return; } }
      const r = await finaliseEvidence(refId);
      setRecord(r.record);
      toast({ title: 'Evidence record finalised' });
    } catch (e) {
      toast({ title: 'Finalise failed', description: e?.response?.data?.detail || e.message });
    } finally { setFinaliseBusy(false); }
  };
  const onReopen = async () => {
    setFinaliseBusy(true);
    try {
      const r = await reopenEvidence(refId);
      setRecord(r.record);
      toast({ title: 'Working draft re-opened for editing' });
    } catch (e) {
      toast({ title: 'Reopen failed', description: e?.response?.data?.detail || e.message });
    } finally { setFinaliseBusy(false); }
  };

  // ---------------- 7-document catalogue ----------------
  const DOCS = [
    {
      no: 1, slug: 'transaction-record',
      title: txVariant === 'invoice' ? 'Invoice' : 'Historical Transaction Record',
      params: { variant: txVariant },
      filename: `${refId}-${txVariant === 'invoice' ? 'invoice' : 'historical-transaction-record'}.pdf`,
      editorKey: 'transaction',
      variantLabel: 'Type', variantValue: txVariant, onVariantChange: setTxVariant,
      variantOptions: [{ value: 'invoice', label: 'Invoice' }, { value: 'historical', label: 'Historical Transaction Record' }],
      testid: 'transaction',
    },
    {
      no: 2, slug: 'payment-receipt',
      title: 'Payment Receipt',
      pillText: isPaid ? 'Available' : 'Payment required',
      params: {},
      filename: `${refId}-payment-receipt.pdf`,
      editorKey: null,
      requiresPaid: true,
      testid: 'payment-receipt',
    },
    {
      no: 3, slug: 'service-agreement',
      title: csaVariant === 'current' ? 'Customer Service Agreement' : 'Historical Customer Service Record',
      params: { variant: csaVariant },
      filename: `${refId}-${csaVariant === 'current' ? 'customer-service-agreement' : 'historical-customer-service-record'}.pdf`,
      editorKey: 'service-agreement',
      variantLabel: 'Type', variantValue: csaVariant, onVariantChange: setCsaVariant,
      variantOptions: [{ value: 'current', label: 'Customer Service Agreement' }, { value: 'historical', label: 'Historical Customer Service Record' }],
      testid: 'service-agreement',
    },
    {
      no: 4, slug: 'service-confirmation-letter',
      title: 'Travel Service Confirmation Letter',
      params: {},
      filename: `${refId}-travel-service-confirmation-letter.pdf`,
      editorKey: 'confirmation-letter',
      testid: 'service-confirmation-letter',
    },
    {
      no: 5, slug: 'itinerary',
      title: 'Singapore Travel Itinerary',
      params: {},
      filename: `${refId}-singapore-travel-itinerary.pdf`,
      editorKey: 'itinerary',
      testid: 'itinerary',
    },
    {
      no: 6, slug: 'booking-summary',
      title: 'Booking Summary',
      params: {},
      filename: `${refId}-booking-summary.pdf`,
      editorKey: 'booking-summary',
      testid: 'booking-summary',
    },
  ];

  const openPreview = async (doc) => {
    if (doc.requiresPaid && !isPaid) {
      toast({ title: 'Preview unavailable', description: 'Payment must be completed before this document can be previewed.' });
      return;
    }
    setPreviewLoading(true);
    setPreviewDoc(doc);
    setPreviewUrl('');
    try {
      const url = await getEvidencePdfBlobUrl(refId, doc.slug, doc.params);
      setPreviewUrl(url);
    } catch (e) {
      toast({ title: 'Preview failed', description: e?.response?.data?.detail || e.message });
      setPreviewDoc(null);
    } finally {
      setPreviewLoading(false);
    }
  };
  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl('');
    setPreviewDoc(null);
  };

  const downloadPdf = async (doc) => {
    if (doc.requiresPaid && !isPaid) {
      toast({ title: 'PDF unavailable', description: 'Payment must be completed first.' });
      return;
    }
    try {
      await downloadEvidencePdf(refId, doc.slug, doc.filename, doc.params);
      toast({ title: `Downloaded ${doc.filename}` });
    } catch (e) {
      toast({ title: 'Download failed', description: e?.response?.data?.detail || e.message });
    }
  };

  const downloadPack = async () => {
    setPackBusy(true);
    try {
      const info = await downloadEvidencePack(refId, {
        ...packOptions,
        transactionVariant: txVariant,
        serviceAgreementVariant: csaVariant,
      });
      toast({
        title: `Applicant Documents Pack downloaded (${info.entries} files)`,
        description: info.paymentReceiptIncluded
          ? 'Includes selected documents + Payment Receipt + supporting docs.'
          : 'Payment Receipt was NOT included (payment not captured or unchecked).',
      });
    } catch (e) {
      toast({ title: 'Pack download failed', description: e?.response?.data?.detail || e.message });
    } finally { setPackBusy(false); }
  };

  const openEditor = async (doc) => {
    if (locked) {
      toast({ title: 'Record is finalised', description: 'Re-open the working draft to edit.' });
      return;
    }
    setEditorDoc(doc.editorKey);
  };
  const closeEditor = () => setEditorDoc(null);
  const saveEditorAndClose = async () => {
    const ok = await save();
    if (ok) closeEditor();
  };

  if (loading) return (
    <div className="rounded-xl border border-teal-200 bg-teal-50/20 p-4" data-testid="evidence-panel">
      <SkeletonBar />
    </div>
  );
  if (!record || !snapshot) return null;

  return (
    <div className="rounded-xl border border-teal-200 bg-white p-4 space-y-4" data-testid="evidence-panel">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="text-[11px] uppercase tracking-widest text-teal-800 font-semibold flex items-center gap-1.5">
          <ClipboardList size={13} /> Customer / ICA Supporting Documents
        </div>
        <span className="text-[10px] text-neutral-500 ml-auto">
          Created {fmtDate(record.createdAt)} · Updated {fmtDate(record.updatedAt)}
        </span>
      </div>

      {/* Snapshot summary */}
      <div className="rounded-lg border border-neutral-200 bg-neutral-50/60 p-3 grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1.5 text-xs">
        <SnapField label="Applicant" value={snapshot.travelers?.[0]?.fullName || snapshot.fullName} />
        <SnapField label="Passport" value={snapshot.travelers?.[0]?.passportNo || snapshot.passportNo} />
        <SnapField label="Nationality" value={snapshot.travelers?.[0]?.nationality || snapshot.nationality} />
        <SnapField label="Travellers" value={String(snapshot.travelers?.length || 1)} />
        <SnapField label="Arrival" value={fmtDate(snapshot.arrival)} />
        <SnapField label="Departure" value={fmtDate(snapshot.departure)} />
        <SnapField label="Application status" value={String(snapshot.status || 'Not recorded')} />
        <SnapField label="Payment" value={isPaid ? 'Received (paid in full)' : (snapshot.payment?.status || 'Not paid')} tone={isPaid ? 'ok' : 'muted'} />
      </div>

      {/* Draft / Finalised banner */}
      {isDraft ? (
        <div className="rounded-lg border-2 border-red-300 bg-red-50 px-3 py-2.5 flex flex-wrap items-center gap-3" data-testid="evidence-draft-banner">
          <FileWarning size={18} className="text-red-700 shrink-0" />
          <div className="flex-1 min-w-56">
            <div className="text-sm font-bold text-red-800 tracking-wide">INTERNAL WORKING DRAFT — ADMIN REVIEW REQUIRED</div>
            <div className="text-[11px] text-red-700">All generated PDFs will carry a DRAFT watermark until this record is finalised.</div>
          </div>
          <Button
            onClick={onFinalise}
            disabled={finaliseBusy}
            className="h-8 bg-emerald-700 hover:bg-emerald-800 text-white text-xs"
            data-testid="evidence-finalise"
          >
            {finaliseBusy ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <ShieldCheck size={13} className="mr-1.5" />}
            Finalise
          </Button>
        </div>
      ) : (
        <div className="rounded-lg border-2 border-emerald-300 bg-emerald-50 px-3 py-2.5 flex flex-wrap items-center gap-3" data-testid="evidence-finalised-banner">
          <ShieldCheck size={18} className="text-emerald-700 shrink-0" />
          <div className="flex-1 min-w-56">
            <div className="text-sm font-bold text-emerald-800 tracking-wide">RECORD FINALISED</div>
            <div className="text-[11px] text-emerald-700">
              Finalised {fmtDate(record.finalisedAt)} by {record.finalisedBy || 'admin'}. PDFs are watermark-free.
            </div>
          </div>
          <Button
            onClick={onReopen}
            disabled={finaliseBusy}
            variant="outline"
            className="h-8 text-xs"
            data-testid="evidence-reopen"
          >
            {finaliseBusy ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <PenSquare size={13} className="mr-1.5" />}
            Re-open draft
          </Button>
        </div>
      )}

      {dirty && !locked && (
        <div className="flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          <FileWarning size={14} className="shrink-0" />
          <div className="flex-1">You have unsaved edits.</div>
          <Button onClick={save} disabled={saving} size="sm" className="h-7 bg-amber-700 hover:bg-amber-800 text-white text-xs" data-testid="evidence-save">
            {saving ? <Loader2 size={12} className="mr-1.5 animate-spin" /> : <FileCheck size={12} className="mr-1.5" />}
            Save draft
          </Button>
        </div>
      )}

      {/* 7-Document list */}
      <div className="rounded-lg border border-neutral-200 overflow-hidden" data-testid="evidence-doc-list">
        <div className="grid grid-cols-12 items-center gap-2 px-3 py-2 bg-neutral-50 border-b border-neutral-200 text-[10px] uppercase tracking-wider text-neutral-500 font-semibold">
          <div className="col-span-1">#</div>
          <div className="col-span-6">Document</div>
          <div className="col-span-5 text-right">Actions</div>
        </div>
        {DOCS.map((doc) => (
          <DocRow
            key={doc.slug}
            doc={doc}
            isPaid={isPaid}
            onPreview={() => openPreview(doc)}
            onEdit={() => openEditor(doc)}
            onDownload={() => downloadPdf(doc)}
            locked={locked}
          />
        ))}
        {/* Row 7 — Evidence Pack (ZIP, no preview) */}
        <div className="grid grid-cols-12 items-center gap-2 px-3 py-2.5 border-b last:border-b-0 border-neutral-100 bg-teal-50/40" data-testid="evidence-doc-row-pack">
          <div className="col-span-1 text-neutral-500 font-mono text-xs">7</div>
          <div className="col-span-6">
            <div className="text-sm font-medium text-neutral-900 flex items-center gap-2">
              <Package size={14} className="text-teal-700" /> Applicant Documents Pack (ZIP)
            </div>
            <div className="text-[11px] text-neutral-500 mt-0.5">Bundles the selected applicant documents and supporting files into one ZIP.</div>
          </div>
          <div className="col-span-5 flex flex-wrap items-center justify-end gap-1.5">
            <Button
              variant="outline" size="sm"
              onClick={() => setPackEditorOpen(true)}
              className="h-8 text-xs"
              data-testid="evidence-pack-options"
            >
              <SlidersHorizontal size={12} className="mr-1.5" /> Options
            </Button>
            <Button
              size="sm"
              onClick={downloadPack}
              disabled={packBusy}
              className="h-8 bg-teal-700 hover:bg-teal-800 text-white text-xs"
              data-testid="evidence-download-pack"
            >
              {packBusy ? <Loader2 size={12} className="mr-1.5 animate-spin" /> : <Download size={12} className="mr-1.5" />}
              {packBusy ? 'Building…' : 'ZIP'}
            </Button>
          </div>
        </div>
      </div>

      {/* Supporting Documents (customer-uploaded) */}
      <SupportingDocsCard
        refId={refId}
        supporting={supporting}
        reload={loadSupporting}
        locked={locked}
        toast={toast}
      />

      {/* Data-integrity footnote */}
      <div className="text-[10px] text-neutral-500 flex items-center gap-1"><FileCheck size={10} /> Every generated PDF shows the actual preparation date and, where applicable, the historical service or transaction date. Nothing is backdated.</div>

      {/* -------- Preview dialog -------- */}
      {previewDoc && (
        <Dialog open={true} onOpenChange={(open) => !open && closePreview()}>
          <DialogContent className="max-w-5xl w-[95vw] h-[85vh] p-0 gap-0 flex flex-col" data-testid="evidence-preview-dialog">
            <DialogHeader className="px-4 py-3 border-b border-neutral-200 shrink-0 flex flex-row items-center justify-between gap-2">
              <DialogTitle className="text-sm font-semibold">Preview · {previewDoc.title}</DialogTitle>
              <div className="flex items-center gap-1.5 mr-6">
                {previewUrl && (
                  <a href={previewUrl} target="_blank" rel="noreferrer"
                     className="inline-flex items-center gap-1 text-xs text-teal-700 hover:text-teal-800 hover:underline"
                     data-testid="evidence-preview-open-new-tab">
                    <ExternalLink size={12} /> Open in new tab
                  </a>
                )}
                <Button size="sm" variant="outline" className="h-7 text-xs"
                        onClick={() => downloadPdf(previewDoc)}
                        data-testid="evidence-preview-download">
                  <Download size={12} className="mr-1" /> Download
                </Button>
              </div>
            </DialogHeader>
            <div className="flex-1 bg-neutral-100 relative min-h-0">
              {previewLoading || !previewUrl ? (
                <div className="absolute inset-0 flex items-center justify-center text-neutral-500">
                  <Loader2 className="animate-spin mr-2" size={16} /> Rendering PDF…
                </div>
              ) : (
                <iframe
                  title={previewDoc.title}
                  src={previewUrl}
                  className="w-full h-full border-0"
                  data-testid="evidence-preview-iframe"
                />
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* -------- Editor dialogs -------- */}
      {editorDoc === 'transaction' && (
        <TransactionEditor
          record={record} patch={patch} snapshot={snapshot}
          txVariant={txVariant} setTxVariant={setTxVariant}
          onClose={closeEditor} onSave={saveEditorAndClose} saving={saving}
        />
      )}
      {editorDoc === 'service-agreement' && (
        <ServiceAgreementEditor
          record={record} patch={patch}
          csaVariant={csaVariant} setCsaVariant={setCsaVariant}
          onClose={closeEditor} onSave={saveEditorAndClose} saving={saving}
        />
      )}
      {editorDoc === 'confirmation-letter' && (
        <ConfirmationLetterEditor
          record={record} patch={patch}
          onClose={closeEditor} onSave={saveEditorAndClose} saving={saving}
        />
      )}
      {editorDoc === 'itinerary' && (
        <ItineraryEditor
          record={record} patch={patch} snapshot={snapshot}
          onClose={closeEditor} onSave={saveEditorAndClose} saving={saving}
        />
      )}
      {editorDoc === 'booking-summary' && (
        <BookingSummaryEditor
          record={record} patch={patch}
          onClose={closeEditor} onSave={saveEditorAndClose} saving={saving}
        />
      )}

      {/* -------- Pack options dialog -------- */}
      {packEditorOpen && (
        <PackOptionsDialog
          options={packOptions}
          setOptions={setPackOptions}
          txVariant={txVariant} setTxVariant={setTxVariant}
          csaVariant={csaVariant} setCsaVariant={setCsaVariant}
          isPaid={isPaid}
          onClose={() => setPackEditorOpen(false)}
        />
      )}
    </div>
  );
}

function SnapField({ label, value, tone }) {
  const cls = tone === 'ok' ? 'text-emerald-700 font-semibold'
           : tone === 'muted' ? 'text-neutral-500' : 'text-neutral-900 font-medium';
  return (
    <div className="flex flex-col">
      <span className="text-[10px] uppercase tracking-wider text-neutral-500">{label}</span>
      <span className={`text-xs ${cls}`}>{value || 'Not recorded'}</span>
    </div>
  );
}

function DocRow({ doc, isPaid, onPreview, onEdit, onDownload, locked }) {
  const requiresPaidDisabled = doc.requiresPaid && !isPaid;
  return (
    <div className="grid grid-cols-12 items-center gap-2 px-3 py-2.5 border-b last:border-b-0 border-neutral-100 hover:bg-neutral-50/50"
         data-testid={`evidence-doc-row-${doc.testid}`}>
      <div className="col-span-1 text-neutral-500 font-mono text-xs">{doc.no}</div>
      <div className="col-span-6">
        <div className="text-sm font-medium text-neutral-900 flex items-center gap-2 flex-wrap">
          {doc.title}
          {doc.pillText && (
            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wide ${
              requiresPaidDisabled ? 'bg-amber-100 text-amber-800' : 'bg-neutral-100 text-neutral-600'
            }`}>{doc.pillText}</span>
          )}
        </div>
        {doc.variantOptions && (
          <div className="mt-1 inline-flex rounded-md border border-neutral-200 overflow-hidden" data-testid={`evidence-variant-${doc.testid}`}>
            {doc.variantOptions.map(opt => (
              <button
                key={opt.value}
                type="button"
                onClick={() => doc.onVariantChange(opt.value)}
                className={`text-[10px] px-2 py-0.5 transition ${
                  doc.variantValue === opt.value
                    ? 'bg-teal-700 text-white'
                    : 'bg-white text-neutral-600 hover:bg-neutral-50'
                }`}
                data-testid={`evidence-variant-${doc.testid}-${opt.value}`}
              >{opt.label}</button>
            ))}
          </div>
        )}
      </div>
      <div className="col-span-5 flex flex-wrap items-center justify-end gap-1.5">
        <Button
          variant="outline" size="sm" onClick={onPreview}
          disabled={requiresPaidDisabled}
          title={requiresPaidDisabled ? 'Payment must be completed first' : ''}
          className="h-8 text-xs"
          data-testid={`evidence-preview-${doc.testid}`}
        ><Eye size={12} className="mr-1.5" /> Preview</Button>
        {doc.editorKey ? (
          <Button
            variant="outline" size="sm" onClick={onEdit} disabled={locked}
            title={locked ? 'Re-open the draft to edit' : ''}
            className="h-8 text-xs"
            data-testid={`evidence-edit-${doc.testid}`}
          ><PenSquare size={12} className="mr-1.5" /> Edit</Button>
        ) : (
          <span className="text-[10px] text-neutral-400 px-2">Not editable</span>
        )}
        <Button
          size="sm" onClick={onDownload} disabled={requiresPaidDisabled}
          className="h-8 bg-teal-700 hover:bg-teal-800 text-white text-xs"
          data-testid={`evidence-download-${doc.testid}`}
        ><Download size={12} className="mr-1.5" /> PDF</Button>
      </div>
    </div>
  );
}

/* ================================================================ */
/*  Document-specific editor dialogs                                  */
/* ================================================================ */

function EditorShell({ title, subtitle, children, onClose, onSave, saving, testid }) {
  return (
    <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col p-0" data-testid={testid}>
        <DialogHeader className="px-4 py-3 border-b border-neutral-200 space-y-0.5">
          <DialogTitle className="text-sm font-semibold">{title}</DialogTitle>
          {subtitle && <div className="text-[11px] text-neutral-500">{subtitle}</div>}
        </DialogHeader>
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
          {children}
        </div>
        <div className="px-4 py-3 border-t border-neutral-200 flex items-center justify-end gap-2 bg-neutral-50/60">
          <Button variant="outline" size="sm" onClick={onClose} data-testid="editor-cancel">Cancel</Button>
          <Button size="sm" onClick={onSave} disabled={saving}
            className="bg-teal-700 hover:bg-teal-800 text-white" data-testid="editor-save">
            {saving ? <Loader2 size={12} className="mr-1.5 animate-spin" /> : <FileCheck size={12} className="mr-1.5" />}
            Save draft
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ServiceCategoryChips({ selected, onToggle, testidPrefix }) {
  const set = new Set(selected || []);
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={`${testidPrefix}-categories`}>
      {SERVICE_CATEGORIES.map(cat => {
        const active = set.has(cat);
        return (
          <button
            key={cat} type="button" onClick={() => onToggle(cat)}
            className={`text-xs px-2 py-1 rounded-full border transition ${
              active ? 'bg-teal-700 text-white border-teal-700' : 'bg-white text-neutral-700 border-neutral-300 hover:bg-neutral-50'
            }`}
            data-testid={`${testidPrefix}-category-${cat.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
          >{cat}</button>
        );
      })}
    </div>
  );
}

function ServiceDescriptionsBlock({ record, patch, testidPrefix }) {
  const cats = record.serviceCategories || [];
  if (!cats.length) return <div className="text-xs text-neutral-500">Tick at least one service to add a description.</div>;
  return (
    <div className="space-y-2">
      {cats.map(cat => (
        <div key={cat}>
          <Label className="text-xs font-medium text-neutral-700">{cat} — description</Label>
          <Textarea
            rows={2}
            className="mt-1 text-xs"
            value={(record.serviceDescriptions || {})[cat] || ''}
            onChange={(e) => patch((r) => ({
              ...r, serviceDescriptions: { ...(r.serviceDescriptions || {}), [cat]: e.target.value },
            }))}
            placeholder={`Short description of the ${cat} service…`}
            data-testid={`${testidPrefix}-desc-${cat.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
          />
        </div>
      ))}
    </div>
  );
}

// Ticking "Free & Easy Tour Package" auto-adds its three component
// services (per Waan Travel product spec). Admin can untick any of them
// individually if a particular customer did not receive all three.
// Unticking the package does NOT drop the components.
const FREE_AND_EASY_COMPONENTS = ['Hotel Booking', 'Flight Ticket Booking', 'Singapore Visa Application Assistance'];
const toggleServiceCategory = (r, cat) => {
  const cur = new Set(r.serviceCategories || []);
  if (cur.has(cat)) {
    cur.delete(cat);
  } else {
    cur.add(cat);
    if (cat === 'Free & Easy Tour Package') {
      FREE_AND_EASY_COMPONENTS.forEach((c) => cur.add(c));
    }
  }
  return { ...r, serviceCategories: Array.from(cur) };
};

function TransactionEditor({ record, patch, snapshot, txVariant, setTxVariant, onClose, onSave, saving }) {
  const toggleCat = (cat) => patch((r) => toggleServiceCategory(r, cat));
  return (
    <EditorShell
      title="Edit — Invoice / Historical Transaction Record"
      subtitle="Change document type, tick the services included, and add line-item descriptions."
      onClose={onClose} onSave={onSave} saving={saving}
      testid="editor-transaction"
    >
      <div>
        <Label className="text-xs font-medium text-neutral-700">Document type</Label>
        <div className="mt-1 inline-flex rounded-md border border-neutral-200 overflow-hidden">
          {[{ v: 'invoice', l: 'Invoice (current)' }, { v: 'historical', l: 'Historical Transaction Record' }].map(({ v, l }) => (
            <button key={v} type="button" onClick={() => setTxVariant(v)}
              className={`text-xs px-3 py-1.5 ${txVariant === v ? 'bg-teal-700 text-white' : 'bg-white text-neutral-700 hover:bg-neutral-50'}`}
              data-testid={`editor-tx-variant-${v}`}
            >{l}</button>
          ))}
        </div>
      </div>
      <div>
        <Label className="text-xs font-medium text-neutral-700">Services included</Label>
        <div className="mt-1"><ServiceCategoryChips selected={record.serviceCategories} onToggle={toggleCat} testidPrefix="editor-tx" /></div>
      </div>
      <div>
        <Label className="text-xs font-medium text-neutral-700">Service descriptions (optional)</Label>
        <div className="mt-1"><ServiceDescriptionsBlock record={record} patch={patch} testidPrefix="editor-tx" /></div>
      </div>
      <div className="rounded-md bg-neutral-50 border border-neutral-200 p-2 text-[11px] text-neutral-500">
        Payment status, total and line-item amounts are pulled from the immutable application record and cannot be edited here.
      </div>
    </EditorShell>
  );
}

function ServiceAgreementEditor({ record, patch, csaVariant, setCsaVariant, onClose, onSave, saving }) {
  const toggleCat = (cat) => patch((r) => toggleServiceCategory(r, cat));
  return (
    <EditorShell
      title="Edit — Customer Service Agreement / Historical Customer Service Record"
      subtitle="Choose the variant, the services covered, and any additional agreement notes."
      onClose={onClose} onSave={onSave} saving={saving}
      testid="editor-service-agreement"
    >
      <div>
        <Label className="text-xs font-medium text-neutral-700">Document type</Label>
        <div className="mt-1 inline-flex rounded-md border border-neutral-200 overflow-hidden">
          {[{ v: 'current', l: 'Customer Service Agreement' }, { v: 'historical', l: 'Historical Customer Service Record' }].map(({ v, l }) => (
            <button key={v} type="button" onClick={() => setCsaVariant(v)}
              className={`text-xs px-3 py-1.5 ${csaVariant === v ? 'bg-teal-700 text-white' : 'bg-white text-neutral-700 hover:bg-neutral-50'}`}
              data-testid={`editor-csa-variant-${v}`}
            >{l}</button>
          ))}
        </div>
      </div>
      <div>
        <Label className="text-xs font-medium text-neutral-700">Services covered</Label>
        <div className="mt-1"><ServiceCategoryChips selected={record.serviceCategories} onToggle={toggleCat} testidPrefix="editor-csa" /></div>
      </div>
      <div>
        <Label className="text-xs font-medium text-neutral-700">Service descriptions</Label>
        <div className="mt-1"><ServiceDescriptionsBlock record={record} patch={patch} testidPrefix="editor-csa" /></div>
      </div>
      <div>
        <Label className="text-xs font-medium text-neutral-700">Additional notes (optional)</Label>
        <Textarea rows={3} className="mt-1 text-xs"
          value={record.evidenceNotes || ''}
          onChange={(e) => patch({ evidenceNotes: e.target.value })}
          placeholder="Any extra clauses or context to appear on the agreement…"
          data-testid="editor-csa-notes"
        />
      </div>
    </EditorShell>
  );
}

function ConfirmationLetterEditor({ record, patch, onClose, onSave, saving }) {
  const toggleCat = (cat) => patch((r) => toggleServiceCategory(r, cat));
  return (
    <EditorShell
      title="Edit — Travel Service Confirmation Letter"
      subtitle="Choose the services being confirmed and any additional body text."
      onClose={onClose} onSave={onSave} saving={saving}
      testid="editor-confirmation-letter"
    >
      <div>
        <Label className="text-xs font-medium text-neutral-700">Services provided or arranged</Label>
        <div className="mt-1"><ServiceCategoryChips selected={record.serviceCategories} onToggle={toggleCat} testidPrefix="editor-cfl" /></div>
      </div>
      <div>
        <Label className="text-xs font-medium text-neutral-700">Per-service description</Label>
        <div className="mt-1"><ServiceDescriptionsBlock record={record} patch={patch} testidPrefix="editor-cfl" /></div>
      </div>
      <div>
        <Label className="text-xs font-medium text-neutral-700">Additional notes (optional)</Label>
        <Textarea rows={4} className="mt-1 text-xs"
          value={record.evidenceNotes || ''}
          onChange={(e) => patch({ evidenceNotes: e.target.value })}
          placeholder="Free-form paragraph that appears after the services section…"
          data-testid="editor-cfl-notes"
        />
      </div>
    </EditorShell>
  );
}

function ItineraryEditor({ record, patch, snapshot, onClose, onSave, saving }) {
  const addDay = () => patch((r) => {
    const days = [...(r.itinerary?.days || [])];
    const last = days[days.length - 1]?.date || snapshot?.arrival || new Date().toISOString().slice(0, 10);
    let next = last;
    try { const d = new Date(last); d.setDate(d.getDate() + 1); next = d.toISOString().slice(0, 10); } catch { /* keep */ }
    days.push({ date: next, activities: [], note: '' });
    return { ...r, itinerary: { ...(r.itinerary || {}), days } };
  });
  const removeDay = (idx) => patch((r) => {
    const days = [...(r.itinerary?.days || [])];
    days.splice(idx, 1);
    return { ...r, itinerary: { ...(r.itinerary || {}), days } };
  });
  const updateDay = (idx, field, value) => patch((r) => {
    const days = [...(r.itinerary?.days || [])];
    days[idx] = { ...days[idx], [field]: value };
    return { ...r, itinerary: { ...(r.itinerary || {}), days } };
  });
  const addActivity = (idx) => patch((r) => {
    const days = [...(r.itinerary?.days || [])];
    const acts = [...(days[idx].activities || []), ''];
    days[idx] = { ...days[idx], activities: acts };
    return { ...r, itinerary: { ...(r.itinerary || {}), days } };
  });
  const updateActivity = (dIdx, aIdx, value) => patch((r) => {
    const days = [...(r.itinerary?.days || [])];
    const acts = [...(days[dIdx].activities || [])];
    acts[aIdx] = value;
    days[dIdx] = { ...days[dIdx], activities: acts };
    return { ...r, itinerary: { ...(r.itinerary || {}), days } };
  });
  const removeActivity = (dIdx, aIdx) => patch((r) => {
    const days = [...(r.itinerary?.days || [])];
    const acts = [...(days[dIdx].activities || [])];
    acts.splice(aIdx, 1);
    days[dIdx] = { ...days[dIdx], activities: acts };
    return { ...r, itinerary: { ...(r.itinerary || {}), days } };
  });
  const days = record.itinerary?.days || [];
  // Generate a standard Free & Easy plan using the existing arrival/departure
  // dates already seeded in the days array. Uses neutral, independent
  // sightseeing/shopping/leisure wording for middle days. Admin can freely
  // edit the generated programme afterwards. No tours, attractions, transfers
  // or meals are invented.
  const generateFreeAndEasyPlan = () => {
    let base = record.itinerary?.days || [];
    if (base.length === 0) {
      // Fallback: derive from snapshot arrival/departure if the days list is empty
      const arr = snapshot?.arrival;
      const dep = snapshot?.departure;
      if (!arr) return;
      const start = new Date(arr);
      const end = dep ? new Date(dep) : new Date(arr);
      const seeded = [];
      const cursor = new Date(start);
      while (cursor <= end && seeded.length < 30) {
        seeded.push({ date: cursor.toISOString().slice(0, 10), activities: [], note: '' });
        cursor.setDate(cursor.getDate() + 1);
      }
      base = seeded;
    }
    const n = base.length;
    const built = base.map((day, idx) => {
      const isFirst = idx === 0;
      const isLast = idx === n - 1;
      if (n === 1) {
        return {
          ...day,
          activities: [
            'Arrival at Singapore Changi Airport. Proceed to the booked hotel and check in.',
            'Rest of the day at leisure.',
            'Check out from the hotel. Proceed to Singapore Changi Airport according to the confirmed return flight schedule.',
          ],
        };
      }
      if (isFirst) {
        return {
          ...day,
          activities: [
            'Arrival at Singapore Changi Airport. Proceed to the booked hotel and check in.',
            'Rest of the day at leisure.',
          ],
        };
      }
      if (isLast) {
        return {
          ...day,
          activities: [
            'Check out from the hotel.',
            'Proceed to Singapore Changi Airport according to the confirmed return flight schedule.',
          ],
        };
      }
      return {
        ...day,
        activities: [
          'Free & Easy Tour Package — independent sightseeing, shopping and leisure at your own pace.',
        ],
      };
    });
    patch((r) => ({ ...r, itinerary: { ...(r.itinerary || {}), days: built } }));
  };
  return (
    <EditorShell
      title="Edit — Singapore Travel Itinerary"
      subtitle="Add day-by-day activities and accommodation notes."
      onClose={onClose} onSave={onSave} saving={saving}
      testid="editor-itinerary"
    >
      <div>
        <Label className="text-xs font-medium text-neutral-700">Accommodation notes</Label>
        <Textarea rows={2} className="mt-1 text-xs"
          value={record.hotelDetails || ''}
          onChange={(e) => patch({ hotelDetails: e.target.value })}
          placeholder="Hotel name · Booking reference · Dates"
          data-testid="editor-itinerary-hotel"
        />
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <Label className="text-xs font-medium text-neutral-700">Day-by-day plan</Label>
        <div className="ml-auto flex items-center gap-2">
          <Button
            variant="outline" size="sm"
            onClick={generateFreeAndEasyPlan}
            className="h-7 text-xs"
            data-testid="editor-itinerary-generate-free-and-easy"
            title="Populate every day using the arrival/departure dates on file. Admin can edit afterwards."
          >
            <FileCheck size={12} className="mr-1" /> Generate Free & Easy Tour Package Plan
          </Button>
          <Button variant="outline" size="sm" onClick={addDay} className="h-7 text-xs" data-testid="editor-itinerary-add-day">
            <Plus size={12} className="mr-1" /> Add day
          </Button>
        </div>
      </div>
      <div className="space-y-2">
        {days.length === 0 && <div className="text-xs text-neutral-500">No days yet. Add the first day above.</div>}
        {days.map((day, idx) => (
          <div key={idx} className="rounded-md border border-neutral-200 bg-neutral-50/40 p-2" data-testid={`editor-itinerary-day-${idx}`}>
            <div className="flex items-center gap-2 mb-1.5">
              <span className="text-xs font-semibold text-neutral-700 min-w-[3rem]">Day {idx + 1}</span>
              <Input
                type="date" className="h-7 w-40 text-xs"
                value={day.date || ''}
                onChange={(e) => updateDay(idx, 'date', e.target.value)}
                data-testid={`editor-itinerary-date-${idx}`}
              />
              <Button variant="ghost" size="sm" onClick={() => removeDay(idx)}
                className="ml-auto h-7 text-red-600 hover:bg-red-50" data-testid={`editor-itinerary-remove-day-${idx}`}>
                <Trash2 size={12} />
              </Button>
            </div>
            <div className="space-y-1">
              {(day.activities || []).map((a, aIdx) => (
                <div key={aIdx} className="flex items-center gap-2">
                  <Input
                    className="h-7 text-xs flex-1" value={a || ''}
                    onChange={(e) => updateActivity(idx, aIdx, e.target.value)}
                    placeholder="Activity or place visited"
                    data-testid={`editor-itinerary-activity-${idx}-${aIdx}`}
                  />
                  <Button variant="ghost" size="sm" onClick={() => removeActivity(idx, aIdx)}
                    className="h-7 text-neutral-500 hover:text-red-600 hover:bg-red-50">
                    <X size={12} />
                  </Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={() => addActivity(idx)}
                className="h-7 text-xs" data-testid={`editor-itinerary-add-activity-${idx}`}>
                <Plus size={11} className="mr-1" /> Add activity
              </Button>
            </div>
            <Textarea
              rows={1} className="mt-2 text-xs" value={day.note || ''}
              onChange={(e) => updateDay(idx, 'note', e.target.value)}
              placeholder="Optional note for this day"
              data-testid={`editor-itinerary-note-${idx}`}
            />
          </div>
        ))}
      </div>
    </EditorShell>
  );
}

function BookingSummaryEditor({ record, patch, onClose, onSave, saving }) {
  const bookings = record.bookings || [];
  const addBooking = () => patch((r) => ({ ...r, bookings: [...(r.bookings || []), { kind: 'Hotel', ref: '', provider: '', amount: '', currency: 'SGD', date: '', note: '' }] }));
  const updateBooking = (i, field, val) => patch((r) => {
    const b = [...(r.bookings || [])];
    b[i] = { ...b[i], [field]: val };
    return { ...r, bookings: b };
  });
  const removeBooking = (i) => patch((r) => {
    const b = [...(r.bookings || [])];
    b.splice(i, 1);
    return { ...r, bookings: b };
  });
  return (
    <EditorShell
      title="Edit — Booking Summary"
      subtitle="Flight, hotel, tour, transportation and any additional bookings."
      onClose={onClose} onSave={onSave} saving={saving}
      testid="editor-booking-summary"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <div>
          <Label className="text-xs font-medium text-neutral-700">Flight details</Label>
          <Textarea rows={2} className="mt-1 text-xs"
            value={record.flightDetails || ''}
            onChange={(e) => patch({ flightDetails: e.target.value })}
            placeholder="SQ405 SIN → DEL, dates, times"
            data-testid="editor-bks-flight"
          />
        </div>
        <div>
          <Label className="text-xs font-medium text-neutral-700">Hotel details</Label>
          <Textarea rows={2} className="mt-1 text-xs"
            value={record.hotelDetails || ''}
            onChange={(e) => patch({ hotelDetails: e.target.value })}
            placeholder="Hotel · Booking ref · Dates"
            data-testid="editor-bks-hotel"
          />
        </div>
        <div>
          <Label className="text-xs font-medium text-neutral-700">Tour / activity</Label>
          <Textarea rows={2} className="mt-1 text-xs"
            value={record.tourActivity || ''}
            onChange={(e) => patch({ tourActivity: e.target.value })}
            placeholder="Half-day city tour, museum ticket, etc."
            data-testid="editor-bks-tour"
          />
        </div>
        <div>
          <Label className="text-xs font-medium text-neutral-700">Transportation</Label>
          <Textarea rows={2} className="mt-1 text-xs"
            value={record.transportation || ''}
            onChange={(e) => patch({ transportation: e.target.value })}
            placeholder="Airport transfer roundtrip, private car, etc."
            data-testid="editor-bks-transportation"
          />
        </div>
      </div>
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Label className="text-xs font-medium text-neutral-700">Additional bookings</Label>
          <Button variant="outline" size="sm" onClick={addBooking} className="ml-auto h-7 text-xs" data-testid="editor-bks-add-booking">
            <Plus size={12} className="mr-1" /> Add booking
          </Button>
        </div>
        <div className="space-y-2">
          {bookings.length === 0 && <div className="text-xs text-neutral-500">No additional bookings.</div>}
          {bookings.map((b, i) => (
            <div key={i} className="grid grid-cols-6 gap-1.5 rounded-md border border-neutral-200 bg-neutral-50/40 p-2" data-testid={`editor-bks-booking-${i}`}>
              <Input placeholder="Kind" className="h-7 text-xs col-span-1" value={b.kind || ''} onChange={(e) => updateBooking(i, 'kind', e.target.value)} />
              <Input placeholder="Reference" className="h-7 text-xs col-span-2" value={b.ref || ''} onChange={(e) => updateBooking(i, 'ref', e.target.value)} />
              <Input placeholder="Provider" className="h-7 text-xs col-span-2" value={b.provider || ''} onChange={(e) => updateBooking(i, 'provider', e.target.value)} />
              <Button variant="ghost" size="sm" onClick={() => removeBooking(i)} className="h-7 text-red-600 hover:bg-red-50 col-span-1"><Trash2 size={12} /></Button>
              <Input placeholder="Amount" className="h-7 text-xs col-span-1" value={b.amount || ''} onChange={(e) => updateBooking(i, 'amount', e.target.value)} />
              <Input placeholder="Currency" className="h-7 text-xs col-span-1" value={b.currency || ''} onChange={(e) => updateBooking(i, 'currency', e.target.value)} />
              <Input placeholder="YYYY-MM-DD" type="date" className="h-7 text-xs col-span-2" value={b.date || ''} onChange={(e) => updateBooking(i, 'date', e.target.value)} />
              <Input placeholder="Note" className="h-7 text-xs col-span-2" value={b.note || ''} onChange={(e) => updateBooking(i, 'note', e.target.value)} />
            </div>
          ))}
        </div>
      </div>
    </EditorShell>
  );
}

function PackOptionsDialog({ options, setOptions, txVariant, setTxVariant, csaVariant, setCsaVariant, isPaid, onClose }) {
  const toggle = (key) => setOptions((o) => ({ ...o, [key]: !o[key] }));
  return (
    <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg" data-testid="pack-options-dialog">
        <DialogHeader>
          <DialogTitle className="text-sm font-semibold">Applicant Documents Pack — options</DialogTitle>
        </DialogHeader>
        <div className="space-y-2 text-xs">
          {[
            ['includeItinerary', 'Singapore Travel Itinerary'],
            ['includeTransaction', 'Invoice / Historical Transaction Record'],
            ['includeBookingSummary', 'Booking Summary'],
            ['includeServiceAgreement', 'Customer Service Agreement / Historical Customer Service Record'],
            ['includeServiceConfirmationLetter', 'Travel Service Confirmation Letter'],
            ['includePaymentReceipt', `Payment Receipt${isPaid ? '' : ' (requires captured payment)'}`],
            ['includeSupporting', 'Supporting customer documents'],
          ].map(([k, l]) => (
            <label key={k} className="flex items-center gap-2 rounded border border-neutral-200 px-2 py-1.5 hover:bg-neutral-50 cursor-pointer" data-testid={`pack-toggle-${k}`}>
              <input type="checkbox" checked={!!options[k]} onChange={() => toggle(k)} disabled={k === 'includePaymentReceipt' && !isPaid} />
              <span className={k === 'includePaymentReceipt' && !isPaid ? 'text-neutral-400' : ''}>{l}</span>
            </label>
          ))}
        </div>
        <div className="border-t border-neutral-200 pt-3 space-y-2">
          <div>
            <Label className="text-xs font-medium text-neutral-700">Transaction record variant</Label>
            <div className="mt-1 inline-flex rounded-md border border-neutral-200 overflow-hidden">
              {[{ v: 'invoice', l: 'Invoice' }, { v: 'historical', l: 'Historical' }].map(({ v, l }) => (
                <button key={v} type="button" onClick={() => setTxVariant(v)}
                  className={`text-xs px-3 py-1 ${txVariant === v ? 'bg-teal-700 text-white' : 'bg-white text-neutral-700 hover:bg-neutral-50'}`}
                  data-testid={`pack-tx-variant-${v}`}
                >{l}</button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs font-medium text-neutral-700">Service agreement variant</Label>
            <div className="mt-1 inline-flex rounded-md border border-neutral-200 overflow-hidden">
              {[{ v: 'current', l: 'Current' }, { v: 'historical', l: 'Historical' }].map(({ v, l }) => (
                <button key={v} type="button" onClick={() => setCsaVariant(v)}
                  className={`text-xs px-3 py-1 ${csaVariant === v ? 'bg-teal-700 text-white' : 'bg-white text-neutral-700 hover:bg-neutral-50'}`}
                  data-testid={`pack-csa-variant-${v}`}
                >{l}</button>
              ))}
            </div>
          </div>
        </div>
        <div className="flex justify-end pt-2">
          <Button size="sm" onClick={onClose} className="bg-teal-700 hover:bg-teal-800 text-white" data-testid="pack-options-done">Done</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ================================================================ */
/*  Supporting docs manager                                           */
/* ================================================================ */
function SupportingDocsCard({ refId, supporting, reload, locked, toast }) {
  const [kind, setKind] = useState('hotel');
  const [label, setLabel] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileRef = React.useRef(null);
  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await uploadEvidenceSupporting(refId, file, kind, label);
      setLabel('');
      if (fileRef.current) fileRef.current.value = '';
      await reload();
      toast({ title: 'Supporting document uploaded' });
    } catch (err) {
      toast({ title: 'Upload failed', description: err?.response?.data?.detail || err.message });
    } finally { setUploading(false); }
  };
  const onDownload = async (d) => {
    try { await downloadEvidenceSupporting(refId, d.id, d.filename); }
    catch (e) { toast({ title: 'Download failed', description: e?.response?.data?.detail || e.message }); }
  };
  const onDelete = async (d) => {
    if (!window.confirm(`Delete ${d.filename}? This cannot be undone.`)) return;
    try { await deleteEvidenceSupporting(refId, d.id); await reload(); toast({ title: 'Deleted' }); }
    catch (e) { toast({ title: 'Delete failed', description: e?.response?.data?.detail || e.message }); }
  };
  return (
    <div className="rounded-lg border border-neutral-200 bg-white p-3 space-y-2" data-testid="evidence-supporting-docs">
      <div className="text-xs font-semibold text-neutral-700 flex items-center gap-1.5">
        <FilePlus size={12} /> Supporting documents (customer-uploaded)
      </div>
      <div className="grid grid-cols-6 gap-2 items-end">
        <div className="col-span-1">
          <Label className="text-[10px] uppercase tracking-wider text-neutral-500">Kind</Label>
          <Select value={kind} onValueChange={setKind} disabled={locked}>
            <SelectTrigger className="h-8 mt-0.5 text-xs" data-testid="evidence-supporting-kind"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="hotel">Hotel</SelectItem>
              <SelectItem value="flight">Flight</SelectItem>
              <SelectItem value="insurance">Insurance</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="col-span-2">
          <Label className="text-[10px] uppercase tracking-wider text-neutral-500">Label (optional)</Label>
          <Input className="h-8 mt-0.5 text-xs" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Marina Bay Sands"
            data-testid="evidence-supporting-label" disabled={locked} />
        </div>
        <div className="col-span-3">
          <Label className="text-[10px] uppercase tracking-wider text-neutral-500">File</Label>
          <input ref={fileRef} type="file" onChange={onUpload}
            disabled={locked || uploading}
            className="mt-0.5 block w-full text-xs file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:bg-teal-50 file:text-teal-700"
            data-testid="evidence-supporting-upload"
          />
        </div>
      </div>
      <div className="space-y-1">
        {supporting.length === 0 && <div className="text-[11px] text-neutral-500">No supporting documents yet.</div>}
        {supporting.map((d) => (
          <div key={d.id} className="flex items-center gap-2 py-1.5 px-2 rounded border border-neutral-100 bg-neutral-50/50 text-xs" data-testid={`evidence-supporting-item-${d.id}`}>
            <FileText size={12} className="text-neutral-500 shrink-0" />
            <span className="font-medium truncate">{d.filename}</span>
            {d.label && <span className="text-[10px] text-neutral-500">· {d.label}</span>}
            <span className="text-[10px] text-neutral-500 uppercase">{d.kind}</span>
            <div className="ml-auto flex gap-1">
              <Button variant="outline" size="sm" onClick={() => onDownload(d)} className="h-7 text-xs" data-testid={`evidence-supporting-download-${d.id}`}>
                <Download size={11} />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => onDelete(d)} disabled={locked}
                className="h-7 text-red-600 hover:bg-red-50" data-testid={`evidence-supporting-delete-${d.id}`}>
                <Trash2 size={11} />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}



/**
 * Renders one applicant's ICA rectification fields (personal + declarations)
 * inside the admin application dialog. Missing values are rendered as
 * "Not provided" in neutral grey — never as "No" — so legacy applications
 * created before the 2026-02 rectification are unambiguous.
 */
function ApplicantIcaCard({ idx, tv }) {
  const has = (v) => v !== undefined && v !== null && String(v).trim() !== '';
  const yn = (v) => (v === 'yes' ? 'Yes' : v === 'no' ? 'No' : null);
  const val = (v) => has(v) ? String(v) : null;
  const antecedent = [
    ['1. Refused entry / deported (any country)', yn(tv.antecedent1_refusedEntry)],
    ['2. Convicted in a court of law', yn(tv.antecedent2_convicted)],
    ['3. Prohibited from entering Singapore', yn(tv.antecedent3_prohibitedSg)],
    ['4. Entered Singapore under different identity', yn(tv.antecedent4_differentIdentity)],
  ];
  const anyYes = antecedent.some(([, v]) => v === 'Yes');
  const Cell = ({ k, v, highlight }) => (
    <div className="flex items-start gap-2">
      <span className="text-neutral-500 min-w-[190px]">{k}:</span>
      {v === null || v === undefined ? (
        <span className="text-neutral-400 italic">Not provided</span>
      ) : (
        <span className={`font-medium break-words ${highlight === 'yes' ? 'text-amber-800' : 'text-neutral-900'}`}>{v}</span>
      )}
    </div>
  );
  return (
    <div className="rounded-lg border border-neutral-100 bg-neutral-50/40 p-4" data-testid={`admin-traveler-${idx}`}>
      <div className="text-xs uppercase tracking-widest text-teal-800 mb-2">Traveler {idx + 1} — {tv.fullName || 'Unnamed'}</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
        <Cell k="Country of birth" v={val(tv.countryOfBirth) || val(tv.countryOfBirthCode)} />
        <Cell k="State/Province of birth" v={val(tv.stateOfBirth)} />
        <Cell k="City / place of birth" v={val(tv.placeOfBirth)} />
        <Cell k="Occupation (ICA)" v={val(tv.occupation)} />
        <Cell k="Country of embarkation" v={val(tv.countryOfEmbarkationCode)} />
        <Cell k="Nationality / Citizenship" v={val(tv.citizenship)} />
      </div>
      {/* Per-traveler residential address (2026-02). Renders "Not provided"
          in grey italic for legacy applications submitted before per-traveler
          addresses shipped — same "Not provided" convention as the ICA cells. */}
      {(() => {
        const addrLine = [tv.address, tv.city, tv.state, tv.postalCode, tv.country].filter((x) => has(x)).join(', ');
        const shared = tv.sameAsFirstAddress === true;
        return (
          <div className="mt-3 pt-3 border-t border-neutral-200">
            <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-1.5">
              Residential address {shared ? '(same as Applicant 1)' : ''}
            </div>
            {addrLine ? (
              <div className="text-sm text-neutral-900 break-words">{addrLine}</div>
            ) : (
              <span className="text-sm text-neutral-400 italic">Not provided</span>
            )}
          </div>
        );
      })()}
      <div className="mt-3 pt-3 border-t border-neutral-200">
        <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-1.5">Declarations</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
          <Cell
            k="Resided ≥1y in another country (last 5y)"
            v={yn(tv.residedOtherCountries)}
            highlight={tv.residedOtherCountries === 'yes' ? 'yes' : undefined}
          />
          {antecedent.map(([k, v], j) => (
            <Cell key={j} k={k} v={v} highlight={v === 'Yes' ? 'yes' : undefined} />
          ))}
        </div>
        {tv.residedOtherCountries === 'yes' && has(tv.residedOtherCountriesDetails) && (
          <div className="mt-3 rounded-md border border-amber-200 bg-amber-50/60 p-3 text-sm">
            <div className="text-[11px] uppercase tracking-widest text-amber-900 mb-1">5-year residence details</div>
            <div className="text-amber-950 whitespace-pre-wrap">{tv.residedOtherCountriesDetails}</div>
          </div>
        )}
        {anyYes && has(tv.antecedentDetails) && (
          <div className="mt-3 rounded-md border border-amber-200 bg-amber-50/60 p-3 text-sm">
            <div className="text-[11px] uppercase tracking-widest text-amber-900 mb-1">Antecedent details</div>
            <div className="text-amber-950 whitespace-pre-wrap">{tv.antecedentDetails}</div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ================================================================ */
/* CUSTOMERS                                                         */
/* ================================================================ */
function CustomersTab({ onOpen, reloadKey }) {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null); // customer object
  const fetch = async () => {
    setLoading(true);
    try { const r = await listCustomers(); setItems(r.items || []); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetch(); /* eslint-disable-next-line */ }, [reloadKey]);
  useEffect(() => {
    if (!selected) { setDetail(null); return; }
    (async () => { const r = await customerDetail(selected); setDetail(r); })();
  }, [selected]);

  if (loading) return <SkeletonBar />;
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <div className="rounded-2xl border border-neutral-200 bg-white overflow-hidden">
        <div className="p-4 border-b border-neutral-100 font-medium flex items-center justify-between">
          <span>Customers ({items.length})</span>
          <Button variant="outline" size="sm" onClick={fetch}><RefreshCw size={13} className="mr-1.5" /> Refresh</Button>
        </div>
        <div className="max-h-[70vh] overflow-y-auto" data-testid="admin-customers-list">
          {items.length === 0 && <div className="p-6 text-sm text-neutral-500">No customers yet.</div>}
          {items.map((c) => {
            const isSelected = selected === c.email;
            return (
              <div key={c.email} className={`w-full flex items-start gap-2 p-4 border-b last:border-b-0 border-neutral-100 ${isSelected ? 'bg-teal-50/50' : 'hover:bg-teal-50/30'}`} data-testid={`admin-customer-row-${c.email}`}>
                <button onClick={() => setSelected(c.email)} className="flex-1 min-w-0 text-left">
                  <div className="font-medium text-sm truncate">{c.fullName || '—'}</div>
                  <div className="text-xs text-neutral-500 truncate" data-testid={`admin-customer-email-${c.email}`}>{c.display_email || c.email}</div>
                  <div className="text-[11px] text-neutral-500 mt-1">
                    {c.applications} app{c.applications === 1 ? '' : 's'} · ${c.totalSpent} · last {fmtDate(c.lastApplication)}
                  </div>
                  {(c.hasSuccessfulPayment || c.hasProcessingApplication) && (
                    <div className="text-[11px] text-amber-800 mt-1 inline-flex items-center gap-1"><ShieldCheck size={10} /> Genuine customer &mdash; protected</div>
                  )}
                </button>
                <button
                  onClick={() => setDeleteTarget(c)}
                  className="shrink-0 p-1.5 rounded-md text-neutral-400 hover:text-red-600 hover:bg-red-50"
                  title="Delete customer"
                  data-testid={`admin-customer-delete-${c.email}`}
                >
                  <Trash2 size={15} />
                </button>
              </div>
            );
          })}
        </div>
      </div>
      <div className="rounded-2xl border border-neutral-200 bg-white p-4">
        {!selected && <div className="text-sm text-neutral-500">Select a customer to see their applications.</div>}
        {selected && !detail && <SkeletonBar />}
        {detail && (
          <div>
            <div className="text-sm text-neutral-500">Customer</div>
            <div className="font-display text-lg break-all">{selected}</div>
            <div className="mt-4 space-y-2">
              {(detail.items || []).map((a) => (
                <button key={a.ref} onClick={() => onOpen(a.ref)} className="w-full text-left rounded-lg border border-neutral-200 p-3 hover:bg-teal-50/30 flex items-center justify-between">
                  <div>
                    <div className="font-mono text-sm">{a.ref}</div>
                    <div className="text-[11px] text-neutral-500">{fmtDate(a.createdAt)} · ${a.total}</div>
                  </div>
                  <StatusPill status={a.status} />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      {deleteTarget && (
        <CustomerDeleteDialog
          customer={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDeleted={() => { setDeleteTarget(null); if (selected === deleteTarget.email) setSelected(null); fetch(); toast({ title: 'Customer deleted' }); }}
        />
      )}
    </div>
  );
}

/* Customer-delete dialog. Fetches the deletion-impact preview
   (applications, files, contacts) and only enables the destructive
   button if the backend confirms the customer is eligible. When the
   customer has paid/processing history, we show a clear explanation
   dialog instead of pretending Delete would work. */
function CustomerDeleteDialog({ customer, onClose, onDeleted }) {
  const { toast } = useToast();
  const [impact, setImpact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const r = await customerDeletionImpact(customer.email);
        setImpact(r);
      } catch (e) {
        toast({ title: 'Failed to load preview', description: e?.response?.data?.detail || e.message });
        onClose();
      } finally { setLoading(false); }
    })();
  }, [customer.email]);
  const confirm = async () => {
    setBusy(true);
    try {
      await deleteCustomer(customer.email);
      onDeleted();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast({
        title: 'Cannot delete customer',
        description: typeof detail === 'object' ? detail.message : (detail || e.message),
      });
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="customer-delete-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Trash2 size={16} className="text-red-600" /> Delete customer</DialogTitle>
          <DialogDescription className="break-all">{customer.display_email || customer.email}</DialogDescription>
        </DialogHeader>
        {loading || !impact ? (
          <div className="py-8 text-center text-sm text-neutral-500">Loading impact preview…</div>
        ) : !impact.eligibleForDeletion ? (
          <div className="space-y-4">
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="customer-delete-blocked">
              <div className="flex items-start gap-2">
                <Ban size={16} className="mt-0.5 shrink-0" />
                <div>
                  <div className="font-medium">Cannot delete — customer has {impact.hasSuccessfulPayment ? 'paid' : 'processing'} history.</div>
                  <div className="mt-1 text-amber-800">
                    Permanent deletion is reserved for development / test data. Archive individual applications from the Applications tab instead.
                  </div>
                </div>
              </div>
            </div>
            <div className="rounded-lg border border-neutral-200 divide-y divide-neutral-100 text-sm">
              <Row label="Applications" value={impact.totalApplications} />
              <Row label="Uploaded files" value={impact.totalFiles} />
              <Row label="Successful payments" value={impact.hasSuccessfulPayment ? 'Yes' : 'None'} />
              <Row label="Processing applications" value={impact.hasProcessingApplication ? 'Yes' : 'None'} />
              <Row label="Contact history" value={`${impact.contactRecords} kept`} />
            </div>
            <div className="flex justify-end">
              <Button variant="outline" onClick={onClose}>Close</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-800 flex items-start gap-2">
              <AlertCircle size={14} className="mt-0.5 shrink-0" />
              <span>This action cannot be undone. Contact history is preserved.</span>
            </div>
            <div className="rounded-lg border border-neutral-200 divide-y divide-neutral-100 text-sm">
              <Row label="Applications to delete" value={impact.totalApplications} />
              <Row label="Uploaded files" value={impact.totalFiles} />
              <Row label="Successful payments" value="None" />
              <Row label="Contact history" value={`${impact.contactRecords} kept`} />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
              <Button onClick={confirm} disabled={busy} className="bg-red-600 hover:bg-red-700 text-white" data-testid="customer-delete-confirm">
                {busy ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Trash2 size={14} className="mr-1.5" />}
                Delete customer
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ================================================================ */
/* CONTACTS                                                          */
/* ================================================================ */
// Hostinger Webmail (Roundcube) compose URL. The path is documented
// only informally, but Roundcube's canonical compose parameters
// (_task=mail, _action=compose, _to, _subject) have been stable for
// years. Not signing the admin in — Hostinger's own auth flow handles
// that. If the admin is not logged in, they land on the Hostinger
// login page first, then the compose form afterwards.
const HOSTINGER_WEBMAIL_ORIGIN = 'https://webmail.hostinger.com/';
function hostingerReplyUrl(email, subject) {
  const q = new URLSearchParams({
    _task: 'mail', _action: 'compose',
    _to: email || '',
    _subject: `Re: ${subject || ''}`.slice(0, 200),
  }).toString();
  return `${HOSTINGER_WEBMAIL_ORIGIN}?${q}`;
}

function ContactStatusPill({ status }) {
  const s = (status || 'new').toLowerCase();
  const cls = s === 'new' ? 'bg-sky-100 text-sky-900'
    : s === 'read' ? 'bg-neutral-200 text-neutral-800'
    : s === 'replied' ? 'bg-emerald-100 text-emerald-900'
    : 'bg-neutral-100 text-neutral-700';
  const Icon = s === 'replied' ? MailCheck : s === 'read' ? MailOpen : Mail;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${cls}`} data-testid={`contact-status-${s}`}>
      <Icon size={11} /> {s.charAt(0).toUpperCase() + s.slice(1)}
    </span>
  );
}

function ContactsTab() {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [expanded, setExpanded] = useState({}); // { id: true } — the contact is open
  const fetch = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (statusFilter !== 'all') params.status = statusFilter;
      const r = await listContacts(params);
      setItems(r.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { fetch(); /* eslint-disable-next-line */ }, [statusFilter]);

  const openContact = async (c) => {
    setExpanded((e) => ({ ...e, [c.id]: !e[c.id] }));
    // Auto-mark 'new' → 'read' on first open. Best-effort; ignore errors.
    if (!expanded[c.id] && (c.status || 'new') === 'new') {
      try {
        const r = await updateContactStatus(c.id, 'read');
        setItems((arr) => arr.map((x) => x.id === c.id ? r.contact : x));
      } catch { /* ignore */ }
    }
  };

  const markReplied = async (c) => {
    try {
      const r = await updateContactStatus(c.id, 'replied');
      setItems((arr) => arr.map((x) => x.id === c.id ? r.contact : x));
      toast({ title: 'Marked as replied' });
    } catch (e) {
      toast({ title: 'Update failed', description: e?.response?.data?.detail || e.message });
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-neutral-200 bg-white p-4 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[220px]">
          <Label className="text-xs text-neutral-500">Search</Label>
          <Input className="h-10 mt-1" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && fetch()} placeholder="Name, email, subject" data-testid="contacts-search" />
        </div>
        <div>
          <Label className="text-xs text-neutral-500">Status</Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-10 w-40 mt-1" data-testid="contacts-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="read">Read</SelectItem>
              <SelectItem value="replied">Replied</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2 ml-auto">
          <Button variant="outline" onClick={fetch}><Search size={14} className="mr-1.5" /> Search</Button>
          <Button variant="outline" onClick={fetch}><RefreshCw size={14} className="mr-1.5" /> Refresh</Button>
        </div>
      </div>
      {loading ? <SkeletonBar /> : (
        <div className="rounded-2xl border border-neutral-200 bg-white overflow-hidden" data-testid="contacts-list">
          {items.length === 0 && <div className="p-6 text-sm text-neutral-500">No enquiries.</div>}
          {items.map((c) => {
            const status = (c.status || 'new').toLowerCase();
            const isOpen = !!expanded[c.id];
            return (
              <div key={c.id} className="border-b last:border-b-0 border-neutral-100" data-testid={`contact-row-${c.id}`}>
                <button
                  type="button"
                  onClick={() => openContact(c)}
                  className={`w-full text-left p-4 hover:bg-teal-50/30 transition-colors ${status === 'new' ? 'bg-sky-50/30' : ''}`}
                  data-testid={`contact-toggle-${c.id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium">{c.name}</span>
                        <span className="text-neutral-500 text-sm break-all">&lt;{c.email}&gt;</span>
                        <ContactStatusPill status={status} />
                      </div>
                      <div className="text-xs text-neutral-500 mt-0.5">{fmtDate(c.createdAt)} · {c.subject}</div>
                      {!isOpen && <div className="mt-2 text-sm text-neutral-700 truncate">{c.message}</div>}
                    </div>
                    <div className="shrink-0 text-neutral-400"><ArrowRight size={14} className={`transition-transform ${isOpen ? 'rotate-90' : ''}`} /></div>
                  </div>
                </button>
                {isOpen && (
                  <div className="px-4 pb-4">
                    <div className="rounded-lg border border-neutral-100 bg-neutral-50 p-3 text-sm whitespace-pre-wrap" data-testid={`contact-message-${c.id}`}>{c.message}</div>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <a
                        href={hostingerReplyUrl(c.email, c.subject)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-teal-800 text-white text-sm hover:bg-teal-900"
                        data-testid={`contact-reply-${c.id}`}
                      >
                        <Send size={13} /> Reply in Hostinger Webmail
                        <ExternalLink size={12} className="opacity-75" />
                      </a>
                      {status !== 'replied' && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-9"
                          onClick={() => markReplied(c)}
                          data-testid={`contact-mark-replied-${c.id}`}
                        >
                          <MailCheck size={13} className="mr-1.5" /> Mark as replied
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-9 ml-auto text-red-600 border-neutral-200 hover:bg-red-50"
                        onClick={() => setDeleteTarget(c)}
                        data-testid={`contact-delete-${c.id}`}
                      >
                        <Trash2 size={13} className="mr-1.5" /> Delete
                      </Button>
                    </div>
                    {status === 'replied' && c.repliedAt && (
                      <div className="mt-2 text-[11px] text-emerald-800 inline-flex items-center gap-1">
                        <MailCheck size={11} /> Replied {fmtDate(c.repliedAt)} {c.repliedBy ? `· ${c.repliedBy}` : ''}
                      </div>
                    )}
                    {status !== 'new' && c.readAt && (
                      <div className="mt-1 text-[11px] text-neutral-500">First read {fmtDate(c.readAt)}</div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {deleteTarget && (
        <ContactDeleteDialog
          contact={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDeleted={() => { setDeleteTarget(null); toast({ title: 'Contact deleted' }); fetch(); }}
        />
      )}
    </div>
  );
}

function ContactDeleteDialog({ contact, onClose, onDeleted }) {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const confirm = async () => {
    setBusy(true);
    try { await deleteContact(contact.id); onDeleted(); }
    catch (e) { toast({ title: 'Delete failed', description: e?.response?.data?.detail || e.message }); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-sm" data-testid="contact-delete-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Trash2 size={16} className="text-red-600" /> Delete contact</DialogTitle>
          <DialogDescription className="break-all">From <span className="font-medium">{contact.name}</span> &lt;{contact.email}&gt;</DialogDescription>
        </DialogHeader>
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-800 flex items-start gap-2">
          <AlertCircle size={14} className="mt-0.5 shrink-0" />
          <span>This action cannot be undone.</span>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={confirm} disabled={busy} className="bg-red-600 hover:bg-red-700 text-white" data-testid="contact-delete-confirm">
            {busy ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Trash2 size={14} className="mr-1.5" />}
            Delete
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ================================================================ */
/* AFFILIATES                                                        */
/* ================================================================ */
function AffiliatesTab() {
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const fetch = async () => { setLoading(true); const r = await listAffiliatesAdmin({ q: q || undefined }); setItems(r.items || []); setLoading(false); };
  useEffect(() => { fetch(); /* eslint-disable-next-line */ }, []);
  return (
    <div className="space-y-4">
      <div className="flex items-end gap-3">
        <div className="flex-1 max-w-md">
          <Label className="text-xs text-neutral-500">Search partners</Label>
          <Input className="h-10 mt-1" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && fetch()} placeholder="Name, email, code" />
        </div>
        <Button variant="outline" onClick={fetch}><Search size={14} className="mr-1.5" /> Search</Button>
      </div>
      {loading ? <SkeletonBar /> : (
        <div className="rounded-2xl border border-neutral-200 bg-white overflow-hidden">
          <div className="hidden md:grid grid-cols-12 bg-neutral-50 border-b border-neutral-200 text-[11px] uppercase tracking-wider text-neutral-500">
            <div className="col-span-3 p-3">Name / Email</div>
            <div className="col-span-2 p-3">Code</div>
            <div className="col-span-2 p-3">Tier</div>
            <div className="col-span-2 p-3">Country</div>
            <div className="col-span-2 p-3">Joined</div>
            <div className="col-span-1 p-3">Verified</div>
          </div>
          {items.length === 0 && <div className="p-6 text-sm text-neutral-500">No partners yet.</div>}
          {items.map((a) => (
            <div key={a.id} className="grid grid-cols-2 md:grid-cols-12 items-center border-b last:border-b-0 border-neutral-100">
              <div className="col-span-2 md:col-span-3 p-3">
                <div className="text-sm font-medium">{a.name}</div>
                <div className="text-[11px] text-neutral-500">{a.email}</div>
              </div>
              <div className="md:col-span-2 p-3 text-sm font-mono">{a.code}</div>
              <div className="md:col-span-2 p-3 text-sm"><Badge variant="secondary">{a.tier}</Badge></div>
              <div className="md:col-span-2 p-3 text-sm text-neutral-700">{a.country || '—'}</div>
              <div className="md:col-span-2 p-3 text-[11px] text-neutral-500">{fmtDate(a.createdAt)}</div>
              <div className="md:col-span-1 p-3 text-xs">{a.emailVerified ? <span className="text-emerald-700">Verified</span> : <span className="text-amber-700">Pending</span>}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ================================================================ */
/* PAYMENTS                                                          */
/* ================================================================ */
function PaymentsTab({ onOpen }) {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exportBusy, setExportBusy] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  useEffect(() => { (async () => { const r = await listPayments(); setItems(r.items || []); setLoading(false); })(); }, []);

  const exportCsv = async () => {
    setExportBusy(true);
    try {
      const start = startDate ? new Date(startDate).toISOString() : null;
      // Add 1 day to endDate so it's exclusive end (covers all of that day)
      const end = endDate ? new Date(new Date(endDate).getTime() + 86400000).toISOString() : null;
      await downloadRefundCsv(start, end);
      toast({ title: 'Refund CSV exported' });
    } catch (e) {
      toast({ title: 'Export failed', description: e?.response?.data?.detail || e.message });
    } finally { setExportBusy(false); }
  };

  if (loading) return <SkeletonBar />;
  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-neutral-200 bg-white p-4 flex flex-col md:flex-row md:items-end gap-3 md:gap-4" data-testid="refund-export-toolbar">
        <div className="flex-1">
          <div className="font-display text-base text-neutral-900">Export refunds</div>
          <div className="text-xs text-neutral-500 mt-0.5">Download every refund entry as CSV. Filter by date range (optional).</div>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <Label htmlFor="refund-start" className="text-[10px] uppercase tracking-widest text-neutral-500">Start</Label>
            <Input id="refund-start" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="h-9 w-36" data-testid="refund-export-start" />
          </div>
          <div>
            <Label htmlFor="refund-end" className="text-[10px] uppercase tracking-widest text-neutral-500">End</Label>
            <Input id="refund-end" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="h-9 w-36" data-testid="refund-export-end" />
          </div>
          <Button onClick={exportCsv} disabled={exportBusy} className="h-9 bg-neutral-900 hover:bg-neutral-800 text-white" data-testid="refund-export-button">
            {exportBusy ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Download size={14} className="mr-1.5" />}
            Export refund CSV
          </Button>
        </div>
      </div>
      <div className="rounded-2xl border border-neutral-200 bg-white overflow-hidden">
        <div className="hidden md:grid grid-cols-12 bg-neutral-50 border-b border-neutral-200 text-[11px] uppercase tracking-wider text-neutral-500">
          <div className="col-span-2 p-3">Ref</div>
          <div className="col-span-3 p-3">Traveler</div>
          <div className="col-span-2 p-3">Amount</div>
          <div className="col-span-2 p-3">Provider</div>
          <div className="col-span-2 p-3">Status</div>
          <div className="col-span-1 p-3">Date</div>
        </div>
        {items.length === 0 && <div className="p-6 text-sm text-neutral-500">No transactions yet.</div>}
        {items.map((p) => (
          <button key={p.ref} onClick={() => onOpen(p.ref)} className="w-full text-left grid grid-cols-2 md:grid-cols-12 items-center border-b last:border-b-0 border-neutral-100 hover:bg-teal-50/30">
            <div className="col-span-2 md:col-span-2 p-3 font-mono text-sm">{p.ref}</div>
            <div className="col-span-2 md:col-span-3 p-3">
              <div className="text-sm font-medium">{p.data?.fullName}</div>
              <div className="text-[11px] text-neutral-500">{p.data?.email}</div>
            </div>
            <div className="md:col-span-2 p-3 text-sm font-medium">${p.total} {p.currency || 'USD'}</div>
            <div className="md:col-span-2 p-3 text-sm capitalize">{p.payment?.provider} <span className="text-[11px] text-neutral-500">({p.payment?.mode})</span></div>
            <div className="md:col-span-2 p-3 text-sm"><Badge variant={p.payment?.status === 'succeeded' ? 'default' : 'secondary'} className={p.payment?.status === 'succeeded' ? 'bg-emerald-600 hover:bg-emerald-600' : ''}>{p.payment?.status}</Badge></div>
            <div className="md:col-span-1 p-3 text-[11px] text-neutral-500">{fmtDate(p.createdAt)}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

/* ================================================================ */
/* EMAILS                                                            */
/* ================================================================ */
function EmailsTab() {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ to: '', subject: '', html: '' });
  const [busy, setBusy] = useState(false);
  const fetch = async () => { setLoading(true); const r = await listEmails({ pageSize: 100 }); setItems(r.items || []); setLoading(false); };
  useEffect(() => { fetch(); }, []);
  const send = async (e) => {
    e.preventDefault();
    if (!form.to || !form.subject || !form.html) { toast({ title: 'Please complete all fields' }); return; }
    setBusy(true);
    try { const r = await sendManualEmail(form.to, form.subject, form.html); toast({ title: r.ok ? 'Email sent' : 'Send failed' }); setForm({ to: '', subject: '', html: '' }); fetch(); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid md:grid-cols-3 gap-4">
      <div className="md:col-span-2 rounded-2xl border border-neutral-200 bg-white overflow-hidden">
        <div className="p-4 border-b border-neutral-100 flex items-center justify-between">
          <div className="font-medium">Email history</div>
          <Button size="sm" variant="outline" onClick={fetch}><RefreshCw size={14} /></Button>
        </div>
        {loading ? <SkeletonBar /> : (
          <div className="max-h-[70vh] overflow-y-auto">
            {items.length === 0 && <div className="p-6 text-sm text-neutral-500">No email history yet.</div>}
            {items.map((e, i) => (
              <div key={i} className="p-4 border-b last:border-b-0 border-neutral-100">
                <div className="flex items-center justify-between text-sm">
                  <div className="font-medium truncate">{e.subject}</div>
                  <Badge variant={e.status === 'sent' ? 'default' : 'secondary'} className={e.status === 'sent' ? 'bg-emerald-600 hover:bg-emerald-600' : 'bg-red-600 hover:bg-red-600 text-white'}>{e.status}</Badge>
                </div>
                <div className="text-[11px] text-neutral-500 mt-1">{fmtDate(e.createdAt)} · to {(e.to || []).join(', ')} · via {e.provider}</div>
                {e.error && <div className="text-[11px] text-red-600 mt-1">{e.error}</div>}
              </div>
            ))}
          </div>
        )}
      </div>
      <form onSubmit={send} className="rounded-2xl border border-neutral-200 bg-white p-4 space-y-3">
        <div className="font-medium">Send email</div>
        <div><Label>To</Label><Input className="h-10 mt-1" type="email" value={form.to} onChange={(e) => setForm({ ...form, to: e.target.value })} /></div>
        <div><Label>Subject</Label><Input className="h-10 mt-1" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} /></div>
        <div><Label>HTML body</Label><Textarea className="min-h-32 mt-1 font-mono text-xs" value={form.html} onChange={(e) => setForm({ ...form, html: e.target.value })} placeholder="<p>Hello…</p>" /></div>
        <Button type="submit" disabled={busy} className="w-full bg-teal-800 hover:bg-teal-900 text-white">{busy ? (<><Loader2 size={14} className="animate-spin mr-1.5" /> Sending…</>) : (<><Send size={14} className="mr-1.5" /> Send</>)}</Button>
      </form>
    </div>
  );
}

/* ================================================================ */
/* Countries (Accept-Applications toggle)                             */
/* ================================================================ */
function CountriesTab() {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [pending, setPending] = useState({}); // { code: true }
  // Local draft price per country so the admin can edit the input freely
  // without hammering the API on every keystroke. Committed on blur or Enter.
  const [priceDrafts, setPriceDrafts] = useState({});

  // Pricing extras (visa-type add-ons + fixed extras) - both editable and
  // used to compute the live per-country preview.
  const [extrasCfg, setExtrasCfg] = useState({
    visaTypeAddons: { single: 0, double: 40 },
    extras: { noHotelArrangement: 5, noFlightArrangement: 10, sgacAddon: 15, hotelAddon: 5, flightAddon: 10 },
  });
  const [extrasDrafts, setExtrasDrafts] = useState({}); // key -> string
  const [extrasPending, setExtrasPending] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const [cRes, eRes] = await Promise.all([adminListCountries(), adminGetPricingExtras()]);
      setItems(cRes.items || []);
      setExtrasCfg({
        visaTypeAddons: eRes.visaTypeAddons || extrasCfg.visaTypeAddons,
        extras: eRes.extras || extrasCfg.extras,
      });
      setPriceDrafts({});
      setExtrasDrafts({});
    } catch (e) {
      toast({ title: 'Failed to load countries', description: e?.response?.data?.detail || 'Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return items;
    return items.filter((c) => c.name.toLowerCase().includes(term) || c.code.includes(term));
  }, [items, q]);

  const enabledCount = items.filter((c) => c.applicationEnabled).length;

  const toggle = async (country, next) => {
    // Optimistic update - revert on failure.
    setItems((prev) => prev.map((c) => (c.code === country.code ? { ...c, applicationEnabled: next } : c)));
    setPending((p) => ({ ...p, [country.code]: true }));
    try {
      await adminToggleCountry(country.code, next, { name: country.name, priceFrom: country.priceFrom });
      toast({
        title: next ? `${country.name} is now accepting applications` : `${country.name} paused`,
        description: next ? 'Customers of this nationality can submit again.' : 'The SEO page stays live; the apply CTA is replaced with a paused notice.',
      });
    } catch (e) {
      setItems((prev) => prev.map((c) => (c.code === country.code ? { ...c, applicationEnabled: !next } : c)));
      toast({ title: 'Toggle failed', description: e?.response?.data?.detail || 'Please try again.' });
    } finally {
      setPending((p) => { const n = { ...p }; delete n[country.code]; return n; });
    }
  };

  const savePrice = async (country) => {
    const draft = priceDrafts[country.code];
    if (draft === undefined || draft === null || draft === '') return;
    const next = parseInt(draft, 10);
    if (!Number.isFinite(next) || next <= 0) {
      toast({ title: 'Invalid price', description: 'Enter a positive whole-dollar amount in USD.' });
      setPriceDrafts((d) => { const n = { ...d }; delete n[country.code]; return n; });
      return;
    }
    if (next === country.priceFrom) {
      setPriceDrafts((d) => { const n = { ...d }; delete n[country.code]; return n; });
      return;
    }
    const previous = country.priceFrom;
    setItems((prev) => prev.map((c) => (c.code === country.code ? { ...c, priceFrom: next } : c)));
    setPending((p) => ({ ...p, [country.code]: true }));
    try {
      await adminToggleCountry(country.code, country.applicationEnabled, { name: country.name, priceFrom: next });
      toast({ title: `${country.name} price updated`, description: `New base price: USD $${next}. New applications will use this amount.` });
      setPriceDrafts((d) => { const n = { ...d }; delete n[country.code]; return n; });
    } catch (e) {
      setItems((prev) => prev.map((c) => (c.code === country.code ? { ...c, priceFrom: previous } : c)));
      toast({ title: 'Price update failed', description: e?.response?.data?.detail || 'Please try again.' });
    } finally {
      setPending((p) => { const n = { ...p }; delete n[country.code]; return n; });
    }
  };

  const saveExtras = async () => {
    // Build a partial payload from anything that was edited.
    const visaPatch = {};
    const extrasPatch = {};
    for (const k of ['single', 'double']) {
      const v = extrasDrafts[`v:${k}`];
      if (v !== undefined && v !== '' && parseInt(v, 10) !== extrasCfg.visaTypeAddons[k]) {
        const n = parseInt(v, 10);
        if (!Number.isFinite(n) || n < 0) {
          toast({ title: 'Invalid value', description: 'Add-ons must be whole numbers >= 0.' });
          return;
        }
        visaPatch[k] = n;
      }
    }
    for (const k of ['noHotelArrangement', 'noFlightArrangement', 'sgacAddon', 'hotelAddon', 'flightAddon']) {
      const v = extrasDrafts[`e:${k}`];
      if (v !== undefined && v !== '' && parseInt(v, 10) !== extrasCfg.extras[k]) {
        const n = parseInt(v, 10);
        if (!Number.isFinite(n) || n < 0) {
          toast({ title: 'Invalid value', description: 'Extras must be whole numbers >= 0.' });
          return;
        }
        extrasPatch[k] = n;
      }
    }
    if (Object.keys(visaPatch).length === 0 && Object.keys(extrasPatch).length === 0) {
      toast({ title: 'Nothing to save', description: 'No changes detected.' });
      return;
    }
    setExtrasPending(true);
    try {
      const body = {};
      if (Object.keys(visaPatch).length) body.visaTypeAddons = visaPatch;
      if (Object.keys(extrasPatch).length) body.extras = extrasPatch;
      const r = await adminSavePricingExtras(body);
      setExtrasCfg({ visaTypeAddons: r.visaTypeAddons, extras: r.extras });
      setExtrasDrafts({});
      toast({ title: 'Pricing extras saved', description: 'New applications will use the updated values immediately.' });
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || 'Please try again.' });
    } finally {
      setExtrasPending(false);
    }
  };

  // Live per-country preview: what a customer will see on the Country /
  // SEO page + Hero + Eligibility for a Single-entry, 1-traveler quote.
  const previewFor = (country) =>
    (country.priceFrom || 0) + (extrasCfg.visaTypeAddons?.single ?? 0);

  const extraFields = [
    { key: 'noHotelArrangement', label: 'No hotel arrangement', hint: 'Added when the customer selects "No, please arrange one" for Hotel.' },
    { key: 'noFlightArrangement', label: 'No flight itinerary', hint: 'Added when the customer selects "No, please arrange one" for Flight.' },
    { key: 'sgacAddon', label: 'SGAC filing', hint: 'SG Arrival Card filing add-on.' },
    { key: 'hotelAddon', label: 'Hotel add-on', hint: 'Optional booking add-on.' },
    { key: 'flightAddon', label: 'Flight add-on', hint: 'Optional itinerary add-on.' },
  ];

  return (
    <div className="space-y-4" data-testid="countries-tab">
      {/* -------- Visa-type add-ons + fixed extras editor -------- */}
      <div className="rounded-xl border border-neutral-200 bg-white p-5" data-testid="pricing-extras-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-widest text-neutral-500">Visa types &amp; extras (USD)</div>
            <div className="mt-1 text-sm text-neutral-600">Add-on charged on top of the country base price. All in <span className="font-medium">USD</span>. New applications use these values immediately.</div>
          </div>
          <Button onClick={saveExtras} disabled={extrasPending} className="h-9 bg-teal-800 hover:bg-teal-900 text-white" data-testid="save-extras-btn">
            {extrasPending ? (<><Loader2 size={13} className="mr-1.5 animate-spin" /> Saving&hellip;</>) : (<>Save changes</>)}
          </Button>
        </div>

        <div className="mt-5 grid gap-4 md:grid-cols-2">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-2">Visa type add-ons (per traveler)</div>
            <div className="grid grid-cols-2 gap-3">
              <ExtraInput label="Single Entry" cmsKey="v:single" testid="extra-visa-single"
                current={extrasCfg.visaTypeAddons.single}
                draft={extrasDrafts['v:single']}
                onChange={(val) => setExtrasDrafts((d) => ({ ...d, 'v:single': val }))}
                disabled={extrasPending}
              />
              <ExtraInput label="Double Entry" cmsKey="v:double" testid="extra-visa-double"
                current={extrasCfg.visaTypeAddons.double}
                draft={extrasDrafts['v:double']}
                onChange={(val) => setExtrasDrafts((d) => ({ ...d, 'v:double': val }))}
                disabled={extrasPending}
              />
            </div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-2">Fixed extras (per application)</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {extraFields.map((f) => (
                <ExtraInput key={f.key} label={f.label} hint={f.hint} testid={`extra-${f.key}`}
                  current={extrasCfg.extras[f.key]}
                  draft={extrasDrafts[`e:${f.key}`]}
                  onChange={(val) => setExtrasDrafts((d) => ({ ...d, [`e:${f.key}`]: val }))}
                  disabled={extrasPending}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* -------- Header + search -------- */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-neutral-500">Accept applications &amp; pricing by nationality</div>
          <div className="mt-1 text-sm text-neutral-700">
            {loading ? 'Loading…' : (
              <>
                <span className="font-medium text-emerald-700">{enabledCount}</span>
                <span className="text-neutral-500"> of {items.length} countries currently accepting applications. All prices in <span className="font-medium">USD</span>.</span>
              </>
            )}
          </div>
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search country" className="h-10 pl-9 w-64" data-testid="countries-search" />
        </div>
      </div>

      <div className="rounded-xl border border-neutral-200 bg-white overflow-hidden">
        <div className="grid grid-cols-12 px-4 py-2.5 text-[11px] uppercase tracking-widest text-neutral-500 bg-neutral-50 border-b border-neutral-200">
          <div className="col-span-4">Country</div>
          <div className="col-span-1">Code</div>
          <div className="col-span-3">Base price (USD)</div>
          <div className="col-span-2">Preview - Single × 1</div>
          <div className="col-span-2 text-right">Accept applications</div>
        </div>
        {loading && <div className="p-6 text-sm text-neutral-500"><Loader2 size={14} className="inline animate-spin mr-2" /> Loading nationalities…</div>}
        {!loading && filtered.length === 0 && <div className="p-6 text-sm text-neutral-500">No countries match your search.</div>}
        {!loading && filtered.map((c) => {
          const draft = priceDrafts[c.code];
          const draftValue = draft !== undefined ? draft : String(c.priceFrom);
          const dirty = draft !== undefined && parseInt(draft, 10) !== c.priceFrom;
          const previewTotal = previewFor(c);
          return (
            <div key={c.code} className="grid grid-cols-12 px-4 py-3 items-center border-b border-neutral-100 last:border-b-0 hover:bg-neutral-50/60" data-testid={`country-row-${c.code}`}>
              <div className="col-span-4 flex items-center gap-2.5 min-w-0">
                <span className="w-7 h-7 rounded-md bg-neutral-100 text-neutral-700 grid place-items-center text-[10px] uppercase font-semibold shrink-0">{c.code}</span>
                <span className="truncate text-neutral-900 font-medium">{c.name}</span>
              </div>
              <div className="col-span-1 font-mono text-xs uppercase text-neutral-500">{c.code}</div>
              <div className="col-span-3 flex items-center gap-2">
                <div className="relative">
                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400 text-sm select-none pointer-events-none">$</span>
                  <Input
                    type="number" min="1" step="1" inputMode="numeric"
                    className="h-9 w-24 pl-6 pr-2 text-sm"
                    value={draftValue}
                    onChange={(e) => setPriceDrafts((d) => ({ ...d, [c.code]: e.target.value }))}
                    onBlur={() => savePrice(c)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); savePrice(c); }
                      if (e.key === 'Escape') { setPriceDrafts((d) => { const n = { ...d }; delete n[c.code]; return n; }); }
                    }}
                    disabled={!!pending[c.code]}
                    data-testid={`country-price-${c.code}`}
                    aria-label={`Base price for ${c.name} in USD`}
                  />
                </div>
                <span className="text-[11px] text-neutral-500">USD</span>
                {dirty && <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200">Press Enter</span>}
                {pending[c.code] && !dirty && <Loader2 size={12} className="animate-spin text-neutral-400" />}
              </div>
              <div className="col-span-2 text-sm text-neutral-900 font-medium tabular-nums" data-testid={`country-preview-${c.code}`}>
                ${previewTotal} <span className="text-[11px] text-neutral-500 font-normal">USD</span>
              </div>
              <div className="col-span-2 flex items-center justify-end gap-2">
                <span className={`text-[11px] font-medium ${c.applicationEnabled ? 'text-emerald-700' : 'text-neutral-500'}`}>
                  {c.applicationEnabled ? 'Accepting' : 'Paused'}
                </span>
                <Switch
                  checked={!!c.applicationEnabled}
                  onCheckedChange={(v) => toggle(c, !!v)}
                  disabled={!!pending[c.code]}
                  data-testid={`country-toggle-${c.code}`}
                  aria-label={`Toggle ${c.name}`}
                />
              </div>
            </div>
          );
        })}
      </div>

      <div className="text-[11px] text-neutral-500 leading-relaxed">
        Preview shows exactly what a customer sees on the country's SEO page and in the eligibility widget for a Single-Entry visa with one traveler
        (base price + Single-entry add-on). Extras and Double-entry add-ons apply at checkout when selected.
        Existing applications keep the price agreed at submission time.
      </div>
    </div>
  );
}

function ExtraInput({ label, hint, testid, current, draft, onChange, disabled }) {
  const value = draft !== undefined ? draft : String(current ?? 0);
  const dirty = draft !== undefined && parseInt(draft, 10) !== (current ?? 0);
  return (
    <div>
      <label className="text-[11px] text-neutral-700">{label}</label>
      <div className="mt-1 relative">
        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400 text-sm select-none pointer-events-none">$</span>
        <Input type="number" min="0" step="1" inputMode="numeric"
          className="h-9 pl-6 pr-2 text-sm"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          data-testid={testid}
          aria-label={`${label} in USD`}
        />
        {dirty && <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200">edited</span>}
      </div>
      {hint && <div className="mt-1 text-[10px] text-neutral-500 leading-tight">{hint}</div>}
    </div>
  );
}


/* ================================================================ */
/* CMS                                                               */
/* ================================================================ */
function CmsTab() {
  const { toast } = useToast();
  const KEYS = [
    {
      k: 'homepage_hero',
      hint: 'Override the big heading on the home page.',
      example: {
        chip: 'Licensed Singapore Travel Agent - STB Licence TA03795',
        title_line1: 'Singapore eVisa,',
        title_line2: 'delivered to your',
        title_italic: 'inbox',
        subtitle: 'Waan Travel is your local Singapore sponsor. Fully online, no embassy visit.',
      },
    },
    {
      k: 'contact_info',
      hint: 'Override company address, phone, WhatsApp, email everywhere on the site.',
      example: {
        name: 'Waan Travel Pte. Ltd.',
        address: '810B Choa Chu Kang Ave 7, #14-527, Singapore 682810',
        phone: '+65 6519 5014',
        whatsapp: '+65 8959 2280',
        whatsappRaw: '6589592280',
        email: 'admin@waantravel.com',
        license: 'TA03795',
      },
    },
    {
      k: 'countries',
      hint: 'Replace nationality list & prices (set override:true). Empty/false to keep defaults.',
      example: {
        override: true,
        list: [
          { code: 'in', name: 'India', priceFrom: 79 },
          { code: 'cn', name: 'China', priceFrom: 89 },
        ],
      },
    },
    {
      k: 'faqs',
      hint: 'Replace FAQ groups on the FAQ page (set override:true). Empty/false to keep defaults.',
      example: {
        override: true,
        groups: [
          {
            group: 'Getting started',
            items: [
              { q: 'How long does the process take?', a: 'VisaSG estimated processing time: 3 to 5 working days. Processing times are estimates and may vary. Some applications may take longer if additional review or documents are required.' },
            ],
          },
        ],
      },
    },
  ];
  const [active, setActive] = useState('homepage_hero');
  const [json, setJson] = useState('');
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async (k) => {
    setLoading(true);
    try { const r = await cmsGet(k); setJson(JSON.stringify(r.value || {}, null, 2)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(active); }, [active]);

  const save = async () => {
    setBusy(true);
    try {
      const parsed = JSON.parse(json || '{}');
      await cmsPut(active, parsed); toast({ title: 'Saved', description: 'Public site updated. Reload a public page to see changes.' });
    } catch (e) { toast({ title: 'Invalid JSON', description: e.message }); }
    finally { setBusy(false); }
  };

  const reset = async () => {
    if (!window.confirm('Reset this section to defaults? This saves an empty object and the public site falls back to built-in content.')) return;
    setBusy(true);
    try { await cmsPut(active, {}); setJson('{}'); toast({ title: 'Reset to defaults' }); }
    finally { setBusy(false); }
  };

  const loadExample = () => {
    const cur = KEYS.find((k) => k.k === active);
    if (!cur) return;
    setJson(JSON.stringify(cur.example, null, 2));
    toast({ title: 'Example loaded - review then Save' });
  };

  return (
    <div className="grid md:grid-cols-4 gap-4">
      <div className="rounded-2xl border border-neutral-200 bg-white p-3 space-y-1">
        {KEYS.map((k) => (
          <button key={k.k} onClick={() => setActive(k.k)} className={`w-full text-left px-3 py-2 rounded-md text-sm ${active === k.k ? 'bg-teal-50 text-teal-900' : 'hover:bg-neutral-50'}`}>
            <div className="font-medium capitalize">{k.k.replace(/_/g, ' ')}</div>
            <div className="text-[11px] text-neutral-500">{k.hint}</div>
          </button>
        ))}
      </div>
      <div className="md:col-span-3 rounded-2xl border border-neutral-200 bg-white p-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="font-medium capitalize">{active.replace(/_/g, ' ')}</div>
            <div className="text-[11px] text-neutral-500">Structured JSON override. Empty object &#123;&#125; = fall back to built-in defaults.</div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={loadExample} disabled={busy || loading}>Load example</Button>
            <Button variant="outline" onClick={reset} disabled={busy || loading}>Reset to defaults</Button>
            <Button onClick={save} disabled={busy || loading} className="bg-teal-800 hover:bg-teal-900 text-white">{busy ? (<><Loader2 size={14} className="animate-spin mr-1.5" /> Saving…</>) : 'Save'}</Button>
          </div>
        </div>
        {loading ? <SkeletonBar /> : (
          <Textarea value={json} onChange={(e) => setJson(e.target.value)} className="mt-3 min-h-[440px] font-mono text-xs" />
        )}
      </div>
    </div>
  );
}

/* ================================================================ */
/* AUDIT                                                             */
/* ================================================================ */
function AuditTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { (async () => { const r = await listAudit({ pageSize: 100 }); setItems(r.items || []); setLoading(false); })(); }, []);
  if (loading) return <SkeletonBar />;
  return (
    <div className="rounded-2xl border border-neutral-200 bg-white overflow-hidden">
      <div className="hidden md:grid grid-cols-12 bg-neutral-50 border-b border-neutral-200 text-[11px] uppercase tracking-wider text-neutral-500">
        <div className="col-span-2 p-3">When</div>
        <div className="col-span-3 p-3">Admin</div>
        <div className="col-span-3 p-3">Action</div>
        <div className="col-span-2 p-3">Target</div>
        <div className="col-span-2 p-3">Meta</div>
      </div>
      {items.length === 0 && <div className="p-6 text-sm text-neutral-500">No admin activity yet.</div>}
      {items.map((a, i) => (
        <div key={i} className="grid grid-cols-2 md:grid-cols-12 items-start border-b last:border-b-0 border-neutral-100 text-sm">
          <div className="col-span-2 md:col-span-2 p-3 text-[11px] text-neutral-500">{fmtDate(a.createdAt)}</div>
          <div className="col-span-2 md:col-span-3 p-3">{a.adminEmail}</div>
          <div className="md:col-span-3 p-3 font-medium">{a.action}</div>
          <div className="md:col-span-2 p-3 text-neutral-700">{a.targetType} {a.targetId ? `· ${a.targetId}` : ''}</div>
          <div className="md:col-span-2 p-3 text-[11px] text-neutral-500 truncate">{a.meta ? JSON.stringify(a.meta) : ''}</div>
        </div>
      ))}
    </div>
  );
}

function SkeletonBar() {
  return (
    <div className="animate-pulse p-6 space-y-3">
      <div className="h-4 bg-neutral-100 rounded w-1/3"></div>
      <div className="h-4 bg-neutral-100 rounded w-2/3"></div>
      <div className="h-4 bg-neutral-100 rounded w-1/2"></div>
    </div>
  );
}
